import * as Sentry from "@sentry/react";
import { useAuthStore } from "@/stores/auth";
import { API_BASE } from "@/api/config";
import { apiErrorMessage } from "@/lib/apiErrorMessage";
import { ApiError } from "@/lib/apiError";
import type { ExtractionConfidence } from "@/types";

export { API_BASE };

async function request<T>(
  path: string,
  options?: RequestInit & { timeoutMs?: number; expectedUserId?: string },
): Promise<T> {
  // Festival-site networks stall: a page fires ~10 parallel calls and a
  // radio blip freezes the whole connection until our own timer kills them
  // all at once (the Sentry timeout clusters — server p-max is ~5s, the
  // network is the slow part). GETs are idempotent, so retry ONCE on a
  // fresh connection before surfacing an error; the retry usually lands.
  const method = (options?.method ?? "GET").toUpperCase();
  const attempts = method === "GET" ? 2 : 1;
  let lastError: unknown;
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      return await requestOnce<T>(path, options, {
        finalAttempt: attempt === attempts,
      });
    } catch (err) {
      lastError = err;
      const retryable =
        err instanceof Error && err.message === TIMEOUT_MESSAGE;
      if (!retryable || attempt === attempts) throw err;
      await new Promise((resolve) => setTimeout(resolve, 400));
    }
  }
  throw lastError;
}

const TIMEOUT_MESSAGE = "Request timeout — server unreachable";

async function requestOnce<T>(
  path: string,
  options: (RequestInit & { timeoutMs?: number; expectedUserId?: string }) | undefined,
  { finalAttempt }: { finalAttempt: boolean },
): Promise<T> {
  const assertAccount = () => {
    if (options?.expectedUserId && useAuthStore.getState().user?.id !== options.expectedUserId) {
      throw new Error("Account changed during progress sync");
    }
  };
  assertAccount();
  const token = useAuthStore.getState().accessToken;
  const headers: Record<string, string> = {
    ...(options?.headers as Record<string, string>),
  };
  const multipart =
    typeof FormData !== "undefined" && options?.body instanceof FormData;
  if (!multipart) {
    headers["Content-Type"] = "application/json";
  }
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const controller = new AbortController();
  // 30s: generous enough for a stalled-then-recovering mobile connection
  // (the server itself answers in ≤5s) without hanging the UI forever.
  const timeoutMs = options?.timeoutMs ?? 30_000;
  // Multipart uploads can legitimately take several minutes on festival or
  // mobile connections. An explicit 0 keeps the browser/network timeout while
  // retaining the finite default for normal API calls.
  const timeout =
    timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : undefined;

  try {
    let response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
      signal: controller.signal,
    });

    // Auto-refresh on 401. The refresh() call is mutex-protected in the store
    // so concurrent 401s trigger only one POST /auth/refresh.
    if (response.status === 401 && token) {
      assertAccount();
      const refreshed = await useAuthStore.getState().refresh();
      assertAccount();
      if (refreshed) {
        const newToken = useAuthStore.getState().accessToken;
        if (newToken) {
          headers["Authorization"] = `Bearer ${newToken}`;
          response = await fetch(`${API_BASE}${path}`, {
            ...options,
            headers,
            signal: controller.signal,
          });
        }
      }
      // If refresh returned false, logout() was already called; fall through
      // and let the 401 propagate as an error so the caller can react.
    }

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ detail: response.statusText }));
      const apiError = new ApiError(apiErrorMessage(error), response.status);
      if (response.status >= 500) {
        Sentry.captureException(apiError, {
          extra: { path, status: response.status },
        });
      }
      throw apiError;
    }

    Sentry.addBreadcrumb({
      category: "api",
      message: `${options?.method || "GET"} ${path}`,
      level: "info",
    });

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      const timeoutError = new Error(TIMEOUT_MESSAGE);
      // Only the FINAL failure is an incident — a timeout recovered by the
      // GET retry would otherwise flood Sentry with non-events.
      if (finalAttempt) {
        Sentry.captureException(timeoutError, { extra: { path } });
      }
      throw timeoutError;
    }
    throw err;
  } finally {
    if (timeout !== undefined) clearTimeout(timeout);
  }
}

// --- Auth (public, no token required) ---

export async function forgotPassword(email: string): Promise<void> {
  const response = await fetch(`${API_BASE}/auth/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || "Request failed");
  }
}

export async function resetPassword(
  token: string,
  newPassword: string,
): Promise<void> {
  const response = await fetch(`${API_BASE}/auth/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, new_password: newPassword }),
  });
  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || "Request failed");
  }
}

export async function verifyEmail(token: string): Promise<TokenResponse> {
  const response = await fetch(`${API_BASE}/auth/verify-email`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || "Verification failed");
  }
  return response.json();
}

export async function resendVerification(email: string): Promise<void> {
  const response = await fetch(`${API_BASE}/auth/resend-verification`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || "Request failed");
  }
}

// --- Auth ---

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

// --- Password change ---

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ detail: string }> {
  return request<{ detail: string }>("/auth/change-password", {
    method: "PUT",
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });
}

// --- Riders ---

/** Extraction data from the API. Fields vary by rider_type (dj, band,
 *  live_electronic). Canonical typed schemas live in `@/types` — narrow on
 *  `rider_type` and cast when you need a specific shape. `_step` is injected
 *  by the extraction pipeline to report progress. */
export type RiderExtractedData = Record<string, unknown> & { _step?: string };

export interface UploadResponse {
  rider_id: string;
  artist_name: string;
  status: string;
  text_length: number;
}

export interface DuplicateRiderUploadDetail {
  code: "duplicate_rider";
  message: string;
  rider_id: string;
  artist_name?: string | null;
  original_filename?: string | null;
}

/**
 * Preserves the structured FastAPI upload failure without changing the
 * existing caller contract: this still behaves like a normal Error everywhere
 * except flows that deliberately recover a committed duplicate upload.
 */
export class RiderUploadError extends Error {
  readonly status: number;
  readonly detail: unknown;

  constructor(message: string, status: number, detail: unknown) {
    super(message);
    this.name = "RiderUploadError";
    this.status = status;
    this.detail = detail;
  }
}

export function duplicateRiderIdFromUploadError(error: unknown): string | null {
  if (!(error instanceof RiderUploadError) || error.status !== 409) return null;
  const detail = error.detail;
  if (!detail || typeof detail !== "object" || Array.isArray(detail)) return null;
  const candidate = detail as Partial<DuplicateRiderUploadDetail>;
  return candidate.code === "duplicate_rider" &&
    typeof candidate.rider_id === "string" &&
    candidate.rider_id.trim().length > 0
    ? candidate.rider_id
    : null;
}

export interface RiderResponse {
  id: string;
  artist_name: string;
  rider_type: string;
  status: string;
  extracted_data: RiderExtractedData | null;
  confidence_scores: ExtractionConfidence | null;
  extraction_provider: string | null;
  original_filename: string | null;
  original_pdf_available: boolean;
  content_sha256: string | null;
  original_pdf_text: string | null;
  hospitality_data: Record<string, unknown> | null;
  hospitality_confidence: Record<string, unknown> | null;
  created_at: string | null;
  /** Optimistic-concurrency metadata (optional during rolling deploys). */
  updated_at?: string | null;
  payload_version?: string;
}

export interface RiderListItem {
  id: string;
  artist_name: string;
  rider_type: string;
  status: string;
  confidence_scores: ExtractionConfidence | null;
  extraction_provider: string | null;
  original_filename: string | null;
  original_pdf_available: boolean;
  content_sha256: string | null;
  created_at: string | null;
  /** Lightweight lifecycle detail projected by the list endpoint. */
  extraction_step?: string | null;
  /** True when the source contains a Hospitality payload. */
  has_hospitality_data?: boolean;
}

export interface RiderStatusResponse {
  rider_id: string;
  status: string;
  rider_type: string;
  has_extracted_data: boolean;
  step?: string;
  error?: string;
}

export interface UploadQuotaResponse {
  used: number;
  limit: number | null;
  remaining: number | null;
  tier: string;
  period_start: string;
}

export async function getUploadQuota(): Promise<UploadQuotaResponse> {
  return request<UploadQuotaResponse>("/riders/upload-quota");
}

export async function uploadRider(
  file: File,
  artistName?: string,
): Promise<UploadResponse> {
  const token = useAuthStore.getState().accessToken;
  const formData = new FormData();
  formData.append("file", file);
  if (artistName) {
    formData.append("artist_name", artistName);
  }

  const headers: Record<string, string> = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}/riders/upload`, {
    method: "POST",
    body: formData,
    headers,
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    const detail = error.detail;
    const msg =
      typeof detail === "string"
        ? detail
        : typeof detail?.message === "string"
          ? detail.message
          : "Upload failed";
    throw new RiderUploadError(msg, response.status, detail);
  }

  return response.json();
}

export async function importRiderFromUrl(
  url: string,
  artistName?: string,
): Promise<UploadResponse> {
  return request<UploadResponse>("/riders/import-url", {
    method: "POST",
    body: JSON.stringify({ url, artist_name: artistName }),
  });
}

export async function listRiders(
  status?: string,
  orgId?: string | null,
): Promise<RiderListItem[]> {
  const qs = new URLSearchParams();
  if (status) qs.set("status", status);
  if (orgId) qs.set("org_id", orgId);
  const params = qs.size > 0 ? `?${qs.toString()}` : "";
  return request<RiderListItem[]>(`/riders${params}`);
}

export async function getRider(riderId: string): Promise<RiderResponse> {
  return request<RiderResponse>(`/riders/${riderId}`);
}

export async function downloadOriginalRiderPdf(
  riderId: string,
  fallbackFilename = "original-rider.pdf",
): Promise<void> {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`${API_BASE}/riders/${riderId}/original-pdf`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(error.detail || "Download failed");
  }
  const disposition = res.headers.get("content-disposition") ?? "";
  const match = disposition.match(/filename="([^"]+)"/);
  const filename = match?.[1] || fallbackFilename;
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(href), 0);
}

export async function updateRider(
  riderId: string,
  data: Record<string, unknown>,
): Promise<RiderResponse> {
  return request<RiderResponse>(`/riders/${riderId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteRider(riderId: string): Promise<void> {
  return request<void>(`/riders/${riderId}`, { method: "DELETE" });
}

export async function getRiderStatus(
  riderId: string,
): Promise<RiderStatusResponse> {
  return request<RiderStatusResponse>(`/riders/${riderId}/status`);
}

export async function reExtractRider(
  riderId: string,
  riderType?: string,
): Promise<void> {
  // riderType forces the classification for this run (manual correction);
  // omitted → the pipeline auto-classifies as usual.
  await request<{ detail: string }>(`/riders/${riderId}/re-extract`, {
    method: "POST",
    ...(riderType ? { body: JSON.stringify({ rider_type: riderType }) } : {}),
  });
}

export async function batchApproveRiders(
  riderIds: string[],
): Promise<{ approved: number; total: number }> {
  return request<{ approved: number; total: number }>("/riders/batch-approve", {
    method: "POST",
    body: JSON.stringify({ rider_ids: riderIds }),
  });
}

export async function batchDeleteRiders(
  riderIds: string[],
): Promise<{ deleted: number; total: number }> {
  return request<{ deleted: number; total: number }>("/riders/batch-delete", {
    method: "POST",
    body: JSON.stringify({ rider_ids: riderIds }),
  });
}

// --- Equipment catalog ---

export interface EquipmentModelResponse {
  canonical_name: string;
  manufacturer: string;
  category: string;
  aliases: string[];
  family: string | null;
}

export async function listEquipmentModels(
  category?: string,
): Promise<EquipmentModelResponse[]> {
  const qs = new URLSearchParams();
  if (category) qs.set("category", category);
  const params = qs.size > 0 ? `?${qs.toString()}` : "";
  return request<EquipmentModelResponse[]>(`/equipment-models${params}`);
}

// --- Festivals ---

export interface FestivalListItem {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  location: string | null;
  color: string | null;
  extra_dates: string[];
  recurrence: { type: string; day_of_week: number } | null;
  organization_id: string | null;
  stage_count: number;
}

export interface SlotResponse {
  id: string;
  stage_id: string;
  day: string;
  artist_name: string;
  start_time: string | null;
  end_time: string | null;
  start_datetime: string | null;
  end_datetime: string | null;
  rider_id: string | null;
  artist_profile_id: string | null;
  artist_profile_name: string | null;
  sort_order: number;
  dedicated_setup: boolean;
}

export type ActDocumentKind =
  | "technical"
  | "hospitality"
  | "lighting"
  | "stage_plot"
  | "combined";

export interface ActDocumentResponse {
  id: string;
  slot_id: string;
  rider_id: string;
  document_kind: ActDocumentKind;
  priority: number;
  version_label: string | null;
  valid_from: string | null;
  supersedes_id: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  rider_artist_name: string;
  rider_type: string;
  original_filename: string | null;
  rider_status: string;
  /** Public lifecycle fields are optional during rolling API deployments. */
  rider_public_status?: string;
  rider_processing?: boolean;
  rider_extraction_step?: string | null;
  has_hospitality_data?: boolean;
  is_primary: boolean;
}

export type ActDocumentIngestionStatus =
  | "waiting_extraction"
  | "linking"
  | "ready"
  | "failed";

export type ActDocumentIngestionFailurePhase =
  | "dispatch"
  | "extraction"
  | "classification"
  | "link";

export interface ActDocumentIngestionResponse {
  id: string;
  slot_id: string;
  rider_id: string;
  act_document_id: string | null;
  requested_document_kind: ActDocumentKind | null;
  detected_document_kind: ActDocumentKind | null;
  status: ActDocumentIngestionStatus;
  failure_phase: ActDocumentIngestionFailurePhase | null;
  error: string | null;
  attempts: number;
  file_size_bytes: number;
  client_request_id: string | null;
  dismissed: boolean;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  artist_name: string;
  original_filename: string | null;
  rider_status: string;
  rider_public_status: string;
  rider_processing: boolean;
  rider_extraction_step: string | null;
  dispatch_recovery_available: boolean;
}

export interface ActDocumentCreate {
  rider_id: string;
  document_kind: ActDocumentKind;
  priority?: number;
  version_label?: string | null;
  valid_from?: string | null;
  supersedes_id?: string | null;
  make_primary?: boolean;
}

export interface EffectiveRequirementProvenance {
  act_document_id: string;
  rider_id: string;
  document_kind: string;
  priority: number;
  valid_from: string | null;
  confidence: number | null;
  page: number | null;
  source_line: string | null;
}

export interface EffectiveRequirementCandidate {
  act_document_id: string;
  rider_id: string;
  value: unknown;
}

export interface ContradictionResolutionInfo {
  note: string | null;
  resolved_by_name: string | null;
  resolved_at: string;
}

export interface EffectiveRequirementContradiction {
  id: string;
  domain: "technical" | "hospitality" | "lighting" | "stage_plot";
  type: string;
  path: string;
  message: string;
  selected: unknown;
  candidates: EffectiveRequirementCandidate[];
  status: "unresolved" | "resolved";
  resolution?: ContradictionResolutionInfo | null;
}

export interface EffectiveRequirementsResponse {
  id: string;
  slot_id: string;
  technical: Record<string, unknown>;
  hospitality: Record<string, unknown>;
  lighting: Record<string, unknown>;
  stage_plot: Record<string, unknown>;
  provenance: Record<string, EffectiveRequirementProvenance[]>;
  contradictions: EffectiveRequirementContradiction[];
  source_document_ids: string[];
  source_rider_ids: string[];
  input_fingerprint: string;
  merge_version: string;
  computed_at: string;
  updated_at: string;
}

export type ConsoleEquipmentKind = "cdj" | "mixer" | "turntable" | "power" | "custom";
export type ConsolePlacementState = "on_fomet" | "staged" | "missing" | "spare";

export interface ConsoleLayoutItem {
  id: string;
  type: ConsoleEquipmentKind;
  label: string;
  model?: string;
  x_cm: number;
  y_cm: number;
  width_cm?: number;
  depth_cm?: number;
  rotation?: 0 | 90;
  fomet_index?: number;
  placement_state?: ConsolePlacementState;
  source?: "requested" | "manual" | "preset";
}

/** v3: a fomet's own canvas frame — position and size in content cm. */
export interface FometPlacement {
  /** Identity: items keep pointing at their fomet via `fomet_index`. */
  index: number;
  x_cm: number;
  y_cm: number;
  width_cm: number;
  depth_cm: number;
}

/** v3: the (single) Ready-cases area, positionable like a fomet. */
export interface ReadyCasePlacement {
  x_cm: number;
  y_cm: number;
  width_cm: number;
  /** Minimum depth; the rendered area grows with its staged content. */
  depth_cm: number;
}

export interface StageConsoleLayout {
  version?: 1 | 2 | 3;
  // Legacy v1/v2 fields — still written (mirrored from `fomets`) so older
  // readers and the backend console-overflow check keep working.
  fomet_count?: number;
  fomet_width_cm?: number;
  fomet_depth_cm?: number;
  canvas_width_cm?: number;
  canvas_depth_cm?: number;
  /** v3: stored per-fomet frames. Absent on v1/v2 → derived fixed row. */
  fomets?: FometPlacement[];
  /** v3: positionable Ready-cases area. Absent on v1/v2 → end of the row. */
  ready_case?: ReadyCasePlacement;
  items: ConsoleLayoutItem[];
  day_layouts?: Record<string, StageConsoleLayout>;
  updated_at?: string;
  source?: "console_designer";
  fit_status?: "ok" | "warning" | "conflict";
  fit_issues?: string[];
}

export interface FometDimension {
  width_cm: number;
  depth_cm: number;
  layout?: StageConsoleLayout;
}

export interface StageResponse {
  id: string;
  festival_id: string;
  name: string;
  stage_type: string;
  available_equipment: Record<string, unknown>;
  power_capacity: Record<string, unknown>;
  /** Manual spare counts per category/model; absent key = automatic policy. */
  spare_overrides?: Record<string, Record<string, number>> | null;
  fomet_count: number;
  fomet_dimensions: FometDimension[] | null;
  sort_order: number;
  color: string | null;
  /** Stable palette slot (position-independent) driving the auto colour. */
  palette_index: number;
  slots: SlotResponse[];
}

export interface FestivalResponse {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  location: string | null;
  color: string | null;
  slug: string | null;
  is_public: boolean;
  extra_dates: string[];
  /** Crew-only manual days (build/strike buffer) — never programming days:
   *  they must not surface in the schedule grid or header extra-date chips. */
  crew_extra_dates: string[];
  recurrence: { type: string; day_of_week: number } | null;
  venue_address: string | null;
  venue_lat: number | null;
  venue_lng: number | null;
  venue_place_id: string | null;
  timezone: string;
  /** Minutes of changeover prep the crew can do during the previous set. */
  changeover_prep_minutes: number;
  organization_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  stages: StageResponse[];
}

export async function listFestivals(
  orgId?: string | null,
): Promise<FestivalListItem[]> {
  const params = orgId ? `?org_id=${orgId}` : "";
  return request<FestivalListItem[]>(`/festivals${params}`);
}

export async function getFestival(id: string): Promise<FestivalResponse> {
  return request<FestivalResponse>(`/festivals/${id}`);
}

export async function createFestival(data: {
  name: string;
  start_date: string;
  end_date: string;
  location?: string;
  color?: string;
  extra_dates?: string[];
  recurrence?: { type: string; day_of_week: number };
  organization_id?: string;
}): Promise<FestivalResponse> {
  return request<FestivalResponse>("/festivals", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateFestival(
  id: string,
  data: Record<string, unknown>,
): Promise<FestivalResponse> {
  return request<FestivalResponse>(`/festivals/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteFestival(id: string): Promise<void> {
  return request<void>(`/festivals/${id}`, { method: "DELETE" });
}

export async function deleteStage(
  festivalId: string,
  stageId: string,
): Promise<void> {
  return request<void>(`/festivals/${festivalId}/stages/${stageId}`, {
    method: "DELETE",
  });
}

export async function deleteSlot(
  festivalId: string,
  slotId: string,
): Promise<void> {
  return request<void>(`/festivals/${festivalId}/slots/${slotId}`, {
    method: "DELETE",
  });
}

export async function createStage(
  festivalId: string,
  data: {
    name: string;
    stage_type?: string;
    available_equipment?: Record<string, unknown>;
    power_capacity?: Record<string, unknown>;
    fomet_count?: number;
    fomet_dimensions?: FometDimension[];
    color?: string | null;
  },
): Promise<StageResponse> {
  return request<StageResponse>(`/festivals/${festivalId}/stages`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateStage(
  festivalId: string,
  stageId: string,
  data: {
    name?: string;
    stage_type?: string;
    available_equipment?: Record<string, unknown>;
    power_capacity?: Record<string, unknown>;
    spare_overrides?: Record<string, Record<string, number>>;
    fomet_count?: number;
    fomet_dimensions?: FometDimension[];
    color?: string | null;
  },
): Promise<StageResponse> {
  return request<StageResponse>(`/festivals/${festivalId}/stages/${stageId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function reorderStages(
  festivalId: string,
  stageIds: string[],
): Promise<StageResponse[]> {
  return request<StageResponse[]>(`/festivals/${festivalId}/reorder-stages`, {
    method: "PUT",
    body: JSON.stringify({ stage_ids: stageIds }),
  });
}

export async function createSlot(
  festivalId: string,
  stageId: string,
  data: {
    day: string;
    artist_name: string;
    start_time?: string;
    end_time?: string;
    rider_id?: string;
    artist_profile_id?: string | null;
    dedicated_setup?: boolean;
  },
): Promise<SlotResponse> {
  return request<SlotResponse>(
    `/festivals/${festivalId}/stages/${stageId}/slots`,
    {
      method: "POST",
      body: JSON.stringify(data),
    },
  );
}

export async function updateSlot(
  festivalId: string,
  slotId: string,
  data: {
    /** Move to another stage of the same festival (cross-stage drag). */
    stage_id?: string;
    day?: string;
    artist_name?: string;
    start_time?: string | null;
    end_time?: string | null;
    rider_id?: string | null;
    artist_profile_id?: string | null;
    dedicated_setup?: boolean;
  },
): Promise<SlotResponse> {
  return request<SlotResponse>(`/festivals/${festivalId}/slots/${slotId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function listActDocuments(
  festivalId: string,
  slotId: string,
): Promise<ActDocumentResponse[]> {
  return request<ActDocumentResponse[]>(
    `/festivals/${festivalId}/slots/${slotId}/documents`,
  );
}

export async function listActDocumentIngestions(
  festivalId: string,
): Promise<ActDocumentIngestionResponse[]> {
  return request<ActDocumentIngestionResponse[]>(
    `/festivals/${festivalId}/document-ingestions`,
  );
}

export async function createActDocumentIngestion(
  festivalId: string,
  slotId: string,
  file: File,
  options: {
    artistName?: string;
    requestedDocumentKind?: ActDocumentKind | null;
    clientRequestId: string;
  },
): Promise<ActDocumentIngestionResponse> {
  const formData = new FormData();
  formData.append("file", file);
  if (options.artistName) {
    formData.append("artist_name", options.artistName);
  }
  if (options.requestedDocumentKind) {
    formData.append(
      "requested_document_kind",
      options.requestedDocumentKind,
    );
  }
  formData.append("client_request_id", options.clientRequestId);
  return request<ActDocumentIngestionResponse>(
    `/festivals/${festivalId}/slots/${slotId}/document-ingestions`,
    { method: "POST", body: formData, timeoutMs: 0 },
  );
}

export async function updateActDocumentIngestion(
  festivalId: string,
  ingestionId: string,
  data: {
    requested_document_kind?: ActDocumentKind | null;
    dismissed?: boolean;
  },
): Promise<ActDocumentIngestionResponse> {
  return request<ActDocumentIngestionResponse>(
    `/festivals/${festivalId}/document-ingestions/${ingestionId}`,
    { method: "PATCH", body: JSON.stringify(data) },
  );
}

export async function retryActDocumentIngestion(
  festivalId: string,
  ingestionId: string,
): Promise<ActDocumentIngestionResponse> {
  return request<ActDocumentIngestionResponse>(
    `/festivals/${festivalId}/document-ingestions/${ingestionId}/retry`,
    { method: "POST" },
  );
}

export async function getEffectiveRequirements(
  festivalId: string,
  slotId: string,
): Promise<EffectiveRequirementsResponse> {
  return request<EffectiveRequirementsResponse>(
    `/festivals/${festivalId}/slots/${slotId}/effective-requirements`,
  );
}

export async function resolveActContradiction(
  festivalId: string,
  slotId: string,
  contradictionId: string,
  note?: string,
): Promise<EffectiveRequirementsResponse> {
  return request<EffectiveRequirementsResponse>(
    `/festivals/${festivalId}/slots/${slotId}/contradictions/${contradictionId}/resolve`,
    { method: "POST", body: JSON.stringify({ note: note || null }) },
  );
}

export async function reopenActContradiction(
  festivalId: string,
  slotId: string,
  contradictionId: string,
): Promise<EffectiveRequirementsResponse> {
  return request<EffectiveRequirementsResponse>(
    `/festivals/${festivalId}/slots/${slotId}/contradictions/${contradictionId}/resolve`,
    { method: "DELETE" },
  );
}

export interface EquipmentConfirmationEntry {
  item_key: string;
  confirmed_by_name: string | null;
  confirmed_at: string;
}

export async function listEquipmentConfirmations(
  festivalId: string,
): Promise<EquipmentConfirmationEntry[]> {
  return request<EquipmentConfirmationEntry[]>(
    `/intelligence/${festivalId}/equipment-confirmations`,
  );
}

export async function setEquipmentConfirmation(
  festivalId: string,
  itemKey: string,
  confirmed: boolean,
): Promise<EquipmentConfirmationEntry | null> {
  return request<EquipmentConfirmationEntry | null>(
    `/intelligence/${festivalId}/equipment-confirmations`,
    { method: "PUT", body: JSON.stringify({ item_key: itemKey, confirmed }) },
  );
}

export async function createActDocument(
  festivalId: string,
  slotId: string,
  data: ActDocumentCreate,
): Promise<ActDocumentResponse> {
  return request<ActDocumentResponse>(
    `/festivals/${festivalId}/slots/${slotId}/documents`,
    { method: "POST", body: JSON.stringify(data) },
  );
}

export async function updateActDocument(
  festivalId: string,
  slotId: string,
  documentId: string,
  data: Partial<Omit<ActDocumentCreate, "rider_id">>,
): Promise<ActDocumentResponse> {
  return request<ActDocumentResponse>(
    `/festivals/${festivalId}/slots/${slotId}/documents/${documentId}`,
    { method: "PATCH", body: JSON.stringify(data) },
  );
}

export async function deleteActDocument(
  festivalId: string,
  slotId: string,
  documentId: string,
): Promise<void> {
  await request<void>(
    `/festivals/${festivalId}/slots/${slotId}/documents/${documentId}`,
    { method: "DELETE" },
  );
}

export async function reorderSlots(
  festivalId: string,
  stageId: string,
  slotIds: string[],
): Promise<SlotResponse[]> {
  return request<SlotResponse[]>(
    `/festivals/${festivalId}/stages/${stageId}/reorder`,
    {
      method: "PUT",
      body: JSON.stringify({ slot_ids: slotIds }),
    },
  );
}

// --- Intelligence ---

export interface EquipmentCount {
  model: string;
  quantity: number;
  spare: number;
  request_count: number;
  /** Family rollup: original requested model → qty asked. Empty/missing when single-variant. */
  members?: Record<string, number>;
  sources?: EquipmentRequestSource[];
}

export interface EquipmentRequestSource {
  source_type: "rider" | "effective_requirements" | "production_extra";
  slot_id: string | null;
  rider_id: string | null;
  effective_requirements_id?: string | null;
  source_rider_ids?: string[];
  source_document_ids?: string[];
  artist_name: string;
  stage_name: string;
  day: string;
  start_time: string | null;
  end_time: string | null;
  equipment_type: string;
  model: string;
  original_model: string;
  requested_model: string | null;
  assigned_model: string | null;
  manual_assignment: boolean;
  quantity: number;
  alternatives: string[];
  notes: string | null;
  dedicated_setup: boolean;
}

export interface GenericEquipmentCount {
  category: string;
  item: string;
  quantity: number;
  spare: number;
  provided_by: string | null;
  request_count: number;
  sources?: EquipmentRequestSource[];
}

export interface DayEquipment {
  day: string;
  cdjs: EquipmentCount[];
  mixers: EquipmentCount[];
  turntables: EquipmentCount[];
  band_equipment: GenericEquipmentCount[];
  live_electronic_equipment: GenericEquipmentCount[];
  effects: GenericEquipmentCount[];
}

export interface StageEquipment {
  stage_name: string;
  cdjs: EquipmentCount[];
  mixers: EquipmentCount[];
  turntables: EquipmentCount[];
  band_equipment: GenericEquipmentCount[];
  live_electronic_equipment: GenericEquipmentCount[];
  effects: GenericEquipmentCount[];
  per_day: DayEquipment[];
}

export interface EquipmentTotals {
  cdjs: EquipmentCount[];
  mixers: EquipmentCount[];
  turntables: EquipmentCount[];
  band_equipment: GenericEquipmentCount[];
  live_electronic_equipment: GenericEquipmentCount[];
  effects: GenericEquipmentCount[];
  total_items: number;
  per_stage: StageEquipment[];
}

export type ChangeoverSeverity = "ok" | "tight" | "critical";

export interface ChangeoverSetupItem {
  family: string;
  model: string;
  quantity: number;
  provided_by?: string | null;
  exclusive_use?: boolean | null;
}

export interface ChangeoverArtistSetup {
  artist_name: string;
  rider_type?: string | null;
  items: ChangeoverSetupItem[];
}

export interface Changeover {
  stage_name: string;
  day: string;
  from_artist: string;
  to_artist: string;
  changeover_type: string;
  equipment_added: string[];
  equipment_removed: string[];
  estimated_minutes: number;
  available_gap_minutes: number;
  is_tight: boolean;
  /** Graded against gap + the festival's prep window (crew head start). */
  severity: ChangeoverSeverity;
  fomet_count: number | null;
  requires_soundcheck: boolean;
  soundcheck_minutes: number;
  from_setup?: ChangeoverArtistSetup | null;
  to_setup?: ChangeoverArtistSetup | null;
}

export interface ConflictResponse {
  id: string;
  conflict_type: string;
  severity: string;
  stage_name: string | null;
  day: string | null;
  affected_artists: string[];
  description: string;
  suggestion: string | null;
  resolved: boolean;
}

export interface CrewRequirement {
  stage_name: string;
  day: string;
  from_artist: string;
  to_artist: string;
  changeover_type: string;
  crew_count: number;
  estimated_minutes: number;
  available_gap_minutes: number;
  is_tight: boolean;
  notes: string[];
}

export interface StageCrewSummary {
  stage_id: string | null;
  stage_name: string;
  baseline: number;
  peak: number;
  changeover_count: number;
  tight_count: number;
  details: CrewRequirement[];
}

export interface DayCrewSummary {
  day: string;
  stages_active: number;
  total_crew: number;
  changeover_count: number;
}

export interface StageDayCrewSummary {
  stage_id: string;
  stage_name: string;
  day: string;
  auto_count: number;
  override_count: number | null;
  effective_count: number;
}

export interface CrewNeedsOverrideInput {
  stage_id: string;
  day: string;
  override_count: number;
  note?: string | null;
}

export interface CrewNeedsOverrideResponse {
  id: string;
  festival_id: string;
  stage_id: string;
  day: string;
  override_count: number;
  note: string | null;
}

export interface CrewTotals {
  total_crew: number;
  total_crew_hours: number;
  per_stage: StageCrewSummary[];
  per_day: DayCrewSummary[];
  per_stage_day: StageDayCrewSummary[];
}

export interface OptimizationResult {
  festival_id: string;
  equipment: EquipmentTotals;
  changeovers: Changeover[];
  conflicts: ConflictResponse[];
  crew: CrewTotals | null;
}

export async function optimizeFestival(
  festivalId: string,
): Promise<OptimizationResult> {
  return request<OptimizationResult>(`/intelligence/${festivalId}/optimize`, {
    method: "POST",
  });
}

export async function getEquipment(
  festivalId: string,
): Promise<EquipmentTotals> {
  return request<EquipmentTotals>(`/intelligence/${festivalId}/equipment`);
}

export async function getChangeovers(
  festivalId: string,
): Promise<Changeover[]> {
  return request<Changeover[]>(`/intelligence/${festivalId}/changeovers`);
}

// --- Production timeline ---

export type TimelineKind =
  | "set"
  | "soundcheck"
  | "lobby"
  | "transfer"
  | "press"
  | "meal"
  | "other"
  | "changeover";
export type TimelineSource = "slot" | "schedule_event" | "changeover";

export interface TimelineEntry {
  time: string;
  end_time?: string | null;
  kind: TimelineKind;
  source_type: TimelineSource;
  source_id?: string | null;
  editable?: boolean;
  created_by_role?: "org" | "artist" | null;
  is_guest_artist?: boolean;
  artist_name?: string | null;
  stage_name?: string | null;
  description: string;
  notes?: string | null;
}

export interface TimelineResponse {
  day: string;
  festival_timezone: string;
  entries: TimelineEntry[];
}

export async function getProductionTimeline(
  festivalId: string,
  day: string,
): Promise<TimelineResponse> {
  return request<TimelineResponse>(
    `/festivals/${festivalId}/production-timeline?day=${day}`,
  );
}

export async function getConflicts(
  festivalId: string,
): Promise<ConflictResponse[]> {
  return request<ConflictResponse[]>(`/intelligence/${festivalId}/conflicts`);
}

export async function getCrew(festivalId: string): Promise<CrewTotals> {
  return request<CrewTotals>(`/intelligence/${festivalId}/crew`);
}

export async function upsertCrewNeedsOverride(
  festivalId: string,
  body: CrewNeedsOverrideInput,
): Promise<CrewNeedsOverrideResponse> {
  return request<CrewNeedsOverrideResponse>(
    `/intelligence/${festivalId}/crew/needs-override`,
    { method: "PUT", body: JSON.stringify(body) },
  );
}

export async function deleteCrewNeedsOverride(
  festivalId: string,
  stageId: string,
  day: string,
): Promise<void> {
  const qs = new URLSearchParams({ stage_id: stageId, day }).toString();
  await request(`/intelligence/${festivalId}/crew/needs-override?${qs}`, {
    method: "DELETE",
  });
}

// --- Running order optimization ---

export interface SlotMove {
  slot_id: string;
  artist_name: string;
  from_position: number;
  to_position: number;
}

export interface RunningOrderSuggestion {
  stage_name: string;
  day: string;
  current_order: string[];
  suggested_order: string[];
  current_changeover_minutes: number;
  suggested_changeover_minutes: number;
  time_saved_minutes: number;
  slot_moves: SlotMove[];
}

export interface RunningOrderResult {
  suggestions: RunningOrderSuggestion[];
  total_time_saved_minutes: number;
}

export async function getRunningOrder(
  festivalId: string,
): Promise<RunningOrderResult> {
  return request<RunningOrderResult>(
    `/intelligence/${festivalId}/running-order`,
  );
}

// --- Email drafting ---

export interface EmailDraftRequest {
  conflict_id: string;
  conflict_type: string;
  severity: string;
  stage_name: string | null;
  day: string | null;
  affected_artists: string[];
  description: string;
  suggestion: string | null;
  festival_name: string;
  sender_name: string;
  sender_role: string;
}

export interface EmailDraftResponse {
  subject: string;
  body: string;
  conflict_id: string;
}

export async function draftConflictEmail(
  festivalId: string,
  data: EmailDraftRequest,
): Promise<EmailDraftResponse> {
  return request<EmailDraftResponse>(
    `/intelligence/${festivalId}/draft-email`,
    {
      method: "POST",
      body: JSON.stringify(data),
    },
  );
}

// --- Auto-assign ---

export interface MatchProposal {
  slot_id: string;
  slot_artist_name: string;
  stage_name: string;
  rider_id: string;
  rider_artist_name: string;
  confidence: number;
}

export interface AutoAssignResult {
  proposals: MatchProposal[];
  applied: number;
}

export async function autoAssignRiders(
  festivalId: string,
  apply: boolean = false,
): Promise<AutoAssignResult> {
  return request<AutoAssignResult>(`/intelligence/${festivalId}/auto-assign`, {
    method: "POST",
    body: JSON.stringify({ apply }),
    timeoutMs: 60_000, // heavy batch op; PR-2 makes it fast, this is headroom
  });
}

// --- Billing ---

export async function createCheckoutSession(
  tier: string = "builder",
  billingCycle: "monthly" | "annual" = "monthly",
): Promise<{ checkout_url: string }> {
  return request<{ checkout_url: string }>(
    `/billing/checkout?tier=${tier}&billing_cycle=${billingCycle}`,
    { method: "POST" },
  );
}

export async function getBillingPortal(): Promise<{ portal_url: string }> {
  return request<{ portal_url: string }>("/billing/portal");
}

// --- Usage ---

export async function getUsageCurrent(): Promise<{
  tier: string;
  period_start: string;
  usage: {
    riders: {
      used: number;
      limit: number | null;
      remaining: number | null;
      overage: number;
    };
    festivals: { limit: number | null };
    team_members: { limit: number | null };
    ai_cost_usd: number;
  };
  metering: Record<string, number>;
}> {
  return request("/usage/current");
}

// --- Organizations ---

export interface OrgBrief {
  id: string;
  name: string;
  slug: string;
  is_personal: boolean;
  artist_self_service_enabled: boolean;
  safe_edit_enabled: boolean;
}

export interface MemberResponse {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string | null;
  role: string;
  invite_status: string;
  created_at: string | null;
}

export interface OrganizationResponse {
  id: string;
  name: string;
  slug: string;
  is_personal: boolean;
  artist_self_service_enabled: boolean;
  safe_edit_enabled: boolean;
  created_at: string | null;
}

export interface OrganizationDetailResponse extends OrganizationResponse {
  members: MemberResponse[];
}

export async function listOrganizations(): Promise<OrganizationResponse[]> {
  return request<OrganizationResponse[]>("/organizations");
}

export async function getOrganization(
  orgId: string,
): Promise<OrganizationDetailResponse> {
  return request<OrganizationDetailResponse>(`/organizations/${orgId}`);
}

export async function createOrganization(
  name: string,
): Promise<OrganizationResponse> {
  return request<OrganizationResponse>("/organizations", {
    method: "POST",
    body: JSON.stringify({ name }),
  });
}

export async function updateOrganization(
  orgId: string,
  data: { name?: string; logo_url?: string; safe_edit_enabled?: boolean },
): Promise<OrganizationResponse> {
  return request<OrganizationResponse>(`/organizations/${orgId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteOrganization(orgId: string): Promise<void> {
  await request<void>(`/organizations/${orgId}`, { method: "DELETE" });
}

export async function inviteMember(
  orgId: string,
  email: string,
  role: string = "member",
): Promise<MemberResponse> {
  return request<MemberResponse>(`/organizations/${orgId}/invite`, {
    method: "POST",
    body: JSON.stringify({ email, role }),
  });
}

export async function acceptInvite(token: string): Promise<{ detail: string }> {
  return request<{ detail: string }>(`/organizations/accept-invite/${token}`, {
    method: "POST",
  });
}

export async function updateMemberRole(
  orgId: string,
  memberId: string,
  role: string,
): Promise<MemberResponse> {
  return request<MemberResponse>(
    `/organizations/${orgId}/members/${memberId}`,
    {
      method: "PUT",
      body: JSON.stringify({ role }),
    },
  );
}

export async function removeMember(
  orgId: string,
  memberId: string,
): Promise<void> {
  return request<void>(`/organizations/${orgId}/members/${memberId}`, {
    method: "DELETE",
  });
}

// --- Festival publish ---

export async function publishFestival(
  festivalId: string,
): Promise<{ slug: string; is_public: boolean }> {
  return request<{ slug: string; is_public: boolean }>(
    `/festivals/${festivalId}/publish`,
    {
      method: "PUT",
    },
  );
}

export async function unpublishFestival(
  festivalId: string,
): Promise<{ slug: string; is_public: boolean }> {
  return request<{ slug: string; is_public: boolean }>(
    `/festivals/${festivalId}/unpublish`,
    {
      method: "PUT",
    },
  );
}

// --- Public timetable ---

export interface PublicFestivalListItem {
  name: string;
  slug: string;
  start_date: string;
  end_date: string;
  location: string | null;
  stage_count: number;
  artist_count: number;
}

export async function listPublicFestivals(
  q?: string,
): Promise<PublicFestivalListItem[]> {
  const params = q ? `?q=${encodeURIComponent(q)}` : "";
  const response = await fetch(`${API_BASE}/public/live${params}`);
  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || "Request failed");
  }
  return response.json();
}

export interface PublicSlot {
  artist_name: string;
  day: string;
  start_time: string | null;
  end_time: string | null;
  stage_name: string;
}

export interface PublicStage {
  name: string;
  stage_type: string;
  slots: PublicSlot[];
}

export interface PublicFestival {
  name: string;
  start_date: string;
  end_date: string;
  location: string | null;
  stages: PublicStage[];
}

export async function getPublicTimetable(
  slug: string,
): Promise<PublicFestival> {
  // No auth needed for public endpoints
  const response = await fetch(`${API_BASE}/public/live/${slug}`);
  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || "Not found");
  }
  return response.json();
}

export async function getNowPlaying(slug: string): Promise<PublicSlot[]> {
  const response = await fetch(`${API_BASE}/public/live/${slug}/now`);
  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || "Not found");
  }
  return response.json();
}

// --- Admin ---

export interface PlatformStats {
  user_count: number;
  festival_count: number;
  rider_count: number;
  org_count: number;
  /** Superadmins + the root allowlist — counted whatever the filter says. */
  internal_user_count: number;
  riders_by_status: Record<string, number>;
  users_by_tier: Record<string, number>;
  total_api_cost_this_month: number;
  signups_last_7d: number;
}

export interface AdminUserListItem {
  id: string;
  email: string;
  name: string | null;
  subscription_status: string;
  effective_tier: string;
  is_superadmin: boolean;
  tier_override: boolean;
  created_at: string | null;
  riders_this_month: number;
  riders_monthly_limit: number | null;
  ai_enabled: boolean;
  ai_monthly_limit: number | null;
  ai_messages_this_month: number;
}

export interface AdminUserDetail extends AdminUserListItem {
  is_root_admin: boolean;
  festival_limit: number | null;
  org_count: number;
  festival_count: number;
  rider_count: number;
  api_cost_this_month: number;
  extraction_count: number;
  email_draft_count: number;
  riders_this_month: number;
  riders_monthly_limit: number | null;
  ai_tokens_this_month: number;
}

export interface UsageEntry {
  id: string;
  action: string;
  input_tokens: number;
  output_tokens: number;
  estimated_cost_usd: number;
  rider_id: string | null;
  created_at: string | null;
}

// --- Usage analytics (redesign 2026-07-09) ---

export type UsageRange = "this_month" | "3m" | "6m" | "12m";

export interface UsageTotals {
  cost: number;
  api_calls: number;
  input_tokens: number;
  output_tokens: number;
}

export interface UsageProjection {
  projected_cost: number;
  day_of_month: number;
  days_in_month: number;
}

export interface ActionUsage {
  action: string;
  cost: number;
  api_calls: number;
  input_tokens: number;
  output_tokens: number;
}

export interface UserUsageRow {
  user_id: string;
  email: string;
  tier: string;
  cost: number;
  api_calls: number;
  input_tokens: number;
  output_tokens: number;
}

export interface UsageAnalytics {
  range: string;
  range_start: string;
  totals: UsageTotals;
  projection: UsageProjection | null;
  cost_per_action: ActionUsage[];
  per_user: UserUsageRow[];
}

export interface MonthlyTrend {
  month: string;
  total_cost: number;
  api_calls: number;
  input_tokens: number;
  output_tokens: number;
  by_action: Record<string, number>;
}

export interface UserUsageSummary {
  user_id: string;
  email: string;
  tier: string;
  range: string;
  totals: UsageTotals;
  by_action: ActionUsage[];
}

export interface AdminOrgListItem {
  id: string;
  name: string;
  slug: string;
  is_personal: boolean;
  member_count: number;
  festival_count: number;
  created_at: string | null;
}

// --- Product analytics: signup → checkout funnel (v1.78.0) ---

export interface FunnelWeek {
  /** ISO week label, e.g. "2026-W35" ("total" on the window totals row). */
  week: string;
  /** Monday of the week, ISO date (YYYY-MM-DD). */
  week_start: string;
  /** Distinct users per step. */
  signups: number;
  verified: number;
  created_festival: number;
  uploaded_rider: number;
  hit_paywall: number;
  started_checkout: number;
}

export interface ProductEventRow {
  id: string;
  event: string;
  user_id: string | null;
  user_email: string | null;
  props: Record<string, unknown>;
  created_at: string;
}

export interface FunnelAnalytics {
  weeks: number;
  window_start: string;
  /** Newest week first. */
  series: FunnelWeek[];
  /** Distinct users over the whole window. */
  totals: FunnelWeek;
  /** Latest product events, newest first. */
  recent: ProductEventRow[];
  /** ISO date of the oldest event, backfilled history included. */
  telemetry_since: string;
  /** ISO date from which events were measured live, not reconstructed. */
  live_telemetry_since: string;
}

/** A recently created account and the trail it left, newest first. */
export interface RecentSignup {
  user_id: string;
  email: string;
  name: string | null;
  created_at: string;
  subscription_status: string;
  email_verified: boolean;
  /** From the real tables, so history predating telemetry still counts. */
  festival_count: number;
  rider_count: number;
  /** From product events — honestly `false` for pre-telemetry accounts. */
  hit_paywall: boolean;
}

export interface AdminFestivalListItem {
  id: string;
  name: string;
  /** `null` for festivals created before slugs existed. */
  slug: string | null;
  /** `null` once the owner's account has been deleted. */
  owner_id: string | null;
  owner_email: string | null;
  /** Always set: every festival belongs to an organization (migration 035). */
  organization_id: string;
  organization_name: string | null;
  start_date: string;
  end_date: string;
  is_public: boolean;
  /** Performance stages only — venues are counted separately. */
  stage_count: number;
  venue_count: number;
  slot_count: number;
  rider_count: number;
  created_at: string;
  updated_at: string;
}

export interface AdminFestivalsPage {
  items: AdminFestivalListItem[];
  /** Rows matching the search, across every page. */
  total: number;
}

export async function getAdminStats(
  excludeInternal: boolean = false,
): Promise<PlatformStats> {
  const qs = new URLSearchParams({ exclude_internal: String(excludeInternal) });
  return request<PlatformStats>(`/admin/stats?${qs}`);
}

export async function getAdminUsers(params?: {
  search?: string;
  tier?: string;
  page?: number;
  page_size?: number;
}): Promise<AdminUserListItem[]> {
  const searchParams = new URLSearchParams();
  if (params?.search) searchParams.set("search", params.search);
  if (params?.tier) searchParams.set("tier", params.tier);
  if (params?.page) searchParams.set("page", String(params.page));
  if (params?.page_size) searchParams.set("page_size", String(params.page_size));
  const qs = searchParams.toString();
  return request<AdminUserListItem[]>(`/admin/users${qs ? `?${qs}` : ""}`);
}

export async function getAdminUserDetail(
  userId: string,
): Promise<AdminUserDetail> {
  return request<AdminUserDetail>(`/admin/users/${userId}`);
}

export async function updateAdminUser(
  userId: string,
  data: {
    subscription_status?: string;
    is_superadmin?: boolean;
    tier_override?: boolean;
    festival_limit?: number;
  },
): Promise<AdminUserListItem> {
  return request<AdminUserListItem>(`/admin/users/${userId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function getAdminUserUsage(
  userId: string,
  page: number = 1,
): Promise<UsageEntry[]> {
  return request<UsageEntry[]>(`/admin/users/${userId}/usage?page=${page}`);
}

export async function getAdminUsageAnalytics(
  range: UsageRange = "this_month",
): Promise<UsageAnalytics> {
  return request<UsageAnalytics>(`/admin/usage/analytics?range=${range}`);
}

export async function getAdminUsageTrend(
  months: number = 12,
): Promise<MonthlyTrend[]> {
  return request<MonthlyTrend[]>(`/admin/usage/trend?months=${months}`);
}

export async function getAdminFunnel(
  weeks: number = 8,
  excludeInternal: boolean = false,
): Promise<FunnelAnalytics> {
  const qs = new URLSearchParams({
    weeks: String(weeks),
    exclude_internal: String(excludeInternal),
  });
  return request<FunnelAnalytics>(`/admin/analytics/funnel?${qs}`);
}

export async function getAdminRecentSignups(
  limit: number = 8,
  excludeInternal: boolean = false,
): Promise<RecentSignup[]> {
  const qs = new URLSearchParams({
    limit: String(limit),
    exclude_internal: String(excludeInternal),
  });
  return request<RecentSignup[]>(`/admin/analytics/recent-signups?${qs}`);
}

export async function getAdminFestivals(params: {
  search?: string;
  ownerId?: string;
  page?: number;
  pageSize?: number;
} = {}): Promise<AdminFestivalsPage> {
  const qs = new URLSearchParams();
  if (params.search) qs.set("search", params.search);
  if (params.ownerId) qs.set("owner_id", params.ownerId);
  qs.set("page", String(params.page ?? 1));
  qs.set("page_size", String(params.pageSize ?? 50));
  return request<AdminFestivalsPage>(`/admin/festivals?${qs}`);
}

export async function getAdminEvents(
  limit: number = 50,
  event?: string,
): Promise<ProductEventRow[]> {
  const searchParams = new URLSearchParams();
  searchParams.set("limit", String(limit));
  if (event) searchParams.set("event", event);
  return request<ProductEventRow[]>(
    `/admin/analytics/events?${searchParams.toString()}`,
  );
}

export async function getAdminUserUsageSummary(
  userId: string,
  range: UsageRange = "this_month",
): Promise<UserUsageSummary> {
  return request<UserUsageSummary>(
    `/admin/users/${userId}/usage/summary?range=${range}`,
  );
}

export async function getAdminOrganizations(
  page: number = 1,
): Promise<AdminOrgListItem[]> {
  return request<AdminOrgListItem[]>(`/admin/organizations?page=${page}`);
}

export async function impersonateUser(
  userId: string,
): Promise<{ access_token: string }> {
  return request<{ access_token: string }>(`/admin/impersonate/${userId}`, {
    method: "POST",
  });
}

export async function deleteAdminUser(
  userId: string,
): Promise<{ detail: string }> {
  return request<{ detail: string }>(`/admin/users/${userId}`, {
    method: "DELETE",
  });
}

export async function resetDatabase(): Promise<{
  detail: string;
  deleted: Record<string, number>;
}> {
  return request<{ detail: string; deleted: Record<string, number> }>(
    "/admin/reset-database",
    { method: "POST" },
  );
}

export async function seedAirports(): Promise<{
  detail: string;
  before_count: number;
  after_count: number;
}> {
  return request<{ detail: string; before_count: number; after_count: number }>(
    "/admin/seed-airports",
    { method: "POST" },
  );
}

export async function renormalizeEquipment(): Promise<{
  detail: string;
  scanned: number;
  updated: number;
}> {
  return request<{ detail: string; scanned: number; updated: number }>(
    "/admin/renormalize-equipment",
    { method: "POST" },
  );
}

export async function seedCrewRoles(): Promise<{
  orgs_total: number;
  orgs_seeded: number;
  roles_created: number;
}> {
  return request<{
    orgs_total: number;
    orgs_seeded: number;
    roles_created: number;
  }>("/admin/seed-crew-roles", { method: "POST" });
}

export interface AdminActionTokenResponse {
  token_id: string;
  expires_at: string;
}

export async function requestPromoteAdmin(
  userId: string,
): Promise<AdminActionTokenResponse> {
  return request<AdminActionTokenResponse>(`/admin/users/${userId}/promote`, {
    method: "POST",
  });
}

export async function confirmPromoteAdmin(
  userId: string,
  tokenId: string,
  code: string,
): Promise<{ detail: string }> {
  return request<{ detail: string }>(`/admin/users/${userId}/promote/confirm`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token_id: tokenId, code }),
  });
}

export async function requestDemoteAdmin(
  userId: string,
): Promise<AdminActionTokenResponse> {
  return request<AdminActionTokenResponse>(`/admin/users/${userId}/demote`, {
    method: "POST",
  });
}

export async function confirmDemoteAdmin(
  userId: string,
  tokenId: string,
  code: string,
): Promise<{ detail: string }> {
  return request<{ detail: string }>(`/admin/users/${userId}/demote/confirm`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token_id: tokenId, code }),
  });
}

// --- Follow ---

export interface FollowResponse {
  festival_id: string;
  followed_at: string;
}

export interface FollowStatusResponse {
  is_following: boolean;
  follower_count: number;
}

export interface MyFollowItem {
  festival_id: string;
  name: string;
  slug: string;
  start_date: string;
  end_date: string;
  location: string | null;
  followed_at: string;
}

export async function followFestival(slug: string): Promise<FollowResponse> {
  return request<FollowResponse>(`/public/${slug}/follow`, { method: "POST" });
}

export async function unfollowFestival(slug: string): Promise<void> {
  return request<void>(`/public/${slug}/follow`, { method: "DELETE" });
}

export async function getFollowStatus(
  slug: string,
): Promise<FollowStatusResponse> {
  // Works with or without auth — use request() which adds token if available
  return request<FollowStatusResponse>(`/public/${slug}/follow-status`);
}

export async function listMyFollows(): Promise<MyFollowItem[]> {
  return request<MyFollowItem[]>("/public/my-follows");
}

// --- Notifications ---

export interface NotificationResponse {
  id: string;
  type: string;
  title: string;
  body: string;
  channel: string;
  data: Record<string, string> | null;
  is_read: boolean;
  created_at: string;
}

export interface NotificationList {
  notifications: NotificationResponse[];
  unread_count: number;
  total: number;
}

export async function listNotifications(params?: {
  unread_only?: boolean;
  limit?: number;
  offset?: number;
}): Promise<NotificationList> {
  const searchParams = new URLSearchParams();
  if (params?.unread_only) searchParams.set("unread_only", "true");
  if (params?.limit) searchParams.set("limit", String(params.limit));
  if (params?.offset) searchParams.set("offset", String(params.offset));
  const qs = searchParams.toString();
  return request<NotificationList>(`/notifications${qs ? `?${qs}` : ""}`);
}

export async function getUnreadCount(): Promise<{ unread_count: number }> {
  return request<{ unread_count: number }>("/notifications/unread-count");
}

export async function markNotificationsRead(
  ids: string[],
): Promise<{ marked: number }> {
  return request<{ marked: number }>("/notifications/mark-read", {
    method: "POST",
    body: JSON.stringify({ notification_ids: ids }),
  });
}

export async function markAllNotificationsRead(): Promise<{ marked: number }> {
  return request<{ marked: number }>("/notifications/mark-all-read", {
    method: "POST",
  });
}

// --- Notification Preferences ---

export interface NotificationPreferences {
  slot_changes: boolean;
  stage_changes: boolean;
  bookmark_updates: boolean;
  artist_portal: boolean;
  operational_reminders: boolean;
  equipment_changes: boolean;
  channel_in_app: boolean;
  channel_email: boolean;
  channel_push: boolean;
}

export async function getNotificationPreferences(): Promise<NotificationPreferences> {
  return request<NotificationPreferences>("/notifications/preferences");
}

export async function updateNotificationPreferences(
  data: Partial<NotificationPreferences>,
): Promise<NotificationPreferences> {
  return request<NotificationPreferences>("/notifications/preferences", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// --- Web Push ---

export interface PushPublicKeyResponse {
  enabled: boolean;
  public_key: string | null;
}

export interface PushSubscriptionRequest {
  endpoint: string;
  expiration_time: number | null;
  keys: {
    p256dh: string;
    auth: string;
  };
  user_agent: string | null;
}

export interface PushSubscriptionDeleteRequest {
  endpoint: string;
}

export interface PushStatusResponse {
  enabled: boolean;
  subscription_count: number;
}

export async function getPushPublicKey(): Promise<PushPublicKeyResponse> {
  return request<PushPublicKeyResponse>("/notifications/push/public-key");
}

export async function savePushSubscription(
  data: PushSubscriptionRequest,
): Promise<void> {
  await request<unknown>("/notifications/push/subscriptions", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function deletePushSubscription(
  data: PushSubscriptionDeleteRequest,
): Promise<void> {
  await request<unknown>("/notifications/push/subscriptions", {
    method: "DELETE",
    body: JSON.stringify(data),
  });
}

export async function getPushStatus(): Promise<PushStatusResponse> {
  return request<PushStatusResponse>("/notifications/push/status");
}

// --- Bookmarks ---

export interface BookmarkResponse {
  artist_name: string;
  created_at: string;
}

export interface MyProgrammeSlot {
  artist_name: string;
  day: string;
  start_time: string | null;
  end_time: string | null;
  stage_name: string;
}

export interface MyProgrammeResponse {
  name: string;
  start_date: string;
  end_date: string;
  location: string | null;
  bookmarked_artists: string[];
  slots: MyProgrammeSlot[];
}

export async function addBookmark(
  slug: string,
  artistName: string,
): Promise<BookmarkResponse> {
  return request<BookmarkResponse>(`/public/live/${slug}/bookmarks`, {
    method: "POST",
    body: JSON.stringify({ artist_name: artistName }),
  });
}

export async function removeBookmark(
  slug: string,
  artistName: string,
): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(
    `/public/live/${slug}/bookmarks/${encodeURIComponent(artistName)}`,
    {
      method: "DELETE",
    },
  );
}

export async function listBookmarks(slug: string): Promise<BookmarkResponse[]> {
  return request<BookmarkResponse[]>(`/public/live/${slug}/bookmarks`);
}

export async function getMyProgramme(
  slug: string,
): Promise<MyProgrammeResponse> {
  return request<MyProgrammeResponse>(`/public/live/${slug}/my-programme`);
}

// --- Export ---

export async function openAuthedHtml(
  url: string,
  errorFallback = "Export failed",
): Promise<void> {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    // Exports must always be fresh: a cached response serves yesterday's data.
    cache: "no-store",
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(error.detail || errorFallback);
  }
  const blob = await res.blob();
  window.open(URL.createObjectURL(blob), "_blank");
}

export async function exportTransportGantt(
  festivalId: string,
  locale: "en" | "it" = "en",
): Promise<void> {
  const localeQuery = locale === "it" ? "?locale=it" : "";
  await openAuthedHtml(
    `${API_BASE}/festivals/${festivalId}/export/transport-gantt${localeQuery}`,
  );
}

export async function exportPdfReport(
  festivalId: string,
  locale: "en" | "it" = "en",
): Promise<void> {
  const localeQuery = locale === "it" ? "?locale=it" : "";
  await openAuthedHtml(
    `${API_BASE}/festivals/${festivalId}/export/report${localeQuery}`,
  );
}

// --- Logistics ---

export interface AirportWithDistance {
  iata_code: string;
  name: string;
  lat: number;
  lng: number;
  distance_km: number;
  duration_min: number | null;
}

export interface HotelResponseItem {
  id: string;
  festival_id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  place_id: string | null;
  stars: number | null;
  price_per_night: number | null;
  rooms_available: number | null;
  notes: string | null;
  distance_to_venue_km: number | null;
  duration_to_venue_min: number | null;
}

export interface HotelAirportDistanceItem {
  airport_iata: string;
  airport_name: string;
  distance_km: number;
  duration_min: number;
}

export interface HotelWithDistances extends HotelResponseItem {
  airport_distances: HotelAirportDistanceItem[];
  assigned_artists: number;
}

export interface HotelSuggestion {
  name: string;
  address: string;
  lat: number;
  lng: number;
  place_id: string;
  rating: number | null;
  price_level: number | null;
  distance_to_venue_km: number | null;
  photo_url: string | null;
}

export interface HospitalityAccommodation {
  room_type: string | null;
  rooms_count: number | null;
  nights: number | null;
  check_in: string | null;
  check_out: string | null;
  preferences: string[];
}

export interface HospitalityCatering {
  dietary: string[];
  allergies: string[];
  backstage_food: string[];
  backstage_beverages: string[];
  hot_meals_required: boolean | null;
  meal_times: string[];
}

export interface HospitalityDressingRoom {
  people_count: number | null;
  requirements: string[];
  shared_ok: boolean | null;
}

export interface HospitalityGuestList {
  total_guests: number | null;
  vip_guests: number | null;
  plus_ones: boolean | null;
  backstage_passes: number | null;
}

export interface HospitalityGroundTransport {
  vehicle_type: string | null;
  dedicated_driver: boolean | null;
  airport_pickup: boolean | null;
  special_requirements: string[];
}

export interface HospitalityTravelParty {
  total_size: number | null;
  artist_count: number | null;
  crew_count: number | null;
  manager_count: number | null;
  origin_city: string | null;
  home_airport_iata: string | null;
}

export interface HospitalityExtraction {
  artist_name: string;
  accommodation: HospitalityAccommodation | null;
  catering: HospitalityCatering | null;
  dressing_room: HospitalityDressingRoom | null;
  guest_list: HospitalityGuestList | null;
  ground_transport: HospitalityGroundTransport | null;
  travel_party: HospitalityTravelParty | null;
  special_notes: string[];
}

export interface HospitalityArtistCard {
  artist_name: string;
  /** Stage-manager schematic: plain item lists, drinks/food first,
   * allergies flagged. */
  schematic: { label: string; items: string[]; warn: boolean }[];
}

export async function getHospitalityByArtist(
  festivalId: string,
  locale: "en" | "it" = "en",
): Promise<{ artists: HospitalityArtistCard[] }> {
  return request<{ artists: HospitalityArtistCard[] }>(
    `/intelligence/${festivalId}/hospitality/by-artist?locale=${locale}`,
  );
}

export interface AssignmentResponse {
  id: string;
  hotel_id: string;
  festival_id: string;
  artist_name: string;
  rider_id: string | null;
  check_in: string;
  check_out: string;
  room_type: string | null;
  rooms_count: number;
  notes: string | null;
  arrival_datetime: string | null;
  departure_datetime: string | null;
  airport_iata: string | null;
  arrival_flight: string | null;
  departure_flight: string | null;
  hotel_name: string | null;
  hospitality_summary: HospitalityExtraction | null;
}

export interface TransportEvent {
  time: string;
  event_type: string;
  artist_name: string;
  origin: string;
  destination: string;
  duration_min: number;
  notes: string | null;
  shared_with: string[];
}

export interface LogisticsAlert {
  severity: string;
  alert_type: string;
  message: string;
  artist_name: string | null;
  day: string | null;
}

export interface VenueInfo {
  address: string | null;
  lat: number | null;
  lng: number | null;
  place_id: string | null;
}

export interface LogisticsStats {
  total_artists: number;
  assigned_artists: number;
  total_rooms_per_night: Record<string, number>;
  nearest_airport: string | null;
  transfers_today: number;
  active_alerts: number;
}

export interface DayTransportSchedule {
  day: string;
  events: TransportEvent[];
  alerts: LogisticsAlert[];
}

export interface FestivalAirportResponse {
  id: string;
  airport_iata: string;
  airport_name: string;
  distance_km: number;
  sort_order: number;
}

export interface LogisticsSummary {
  venue: VenueInfo;
  airports: AirportWithDistance[];
  preferred_airports: FestivalAirportResponse[];
  hotels: HotelWithDistances[];
  assignments: AssignmentResponse[];
  transport_schedule: DayTransportSchedule[];
  alerts: LogisticsAlert[];
  stats: LogisticsStats;
}

export interface PlaceSuggestion {
  name: string;
  address: string;
  lat: number;
  lng: number;
  place_id: string;
  rating: number | null;
  price_level: number | null;
  photo_url: string | null;
}

export interface PlaceDetails {
  name: string;
  address: string;
  lat: number;
  lng: number;
  place_id: string;
  rating: number | null;
  price_level: number | null;
  phone: string | null;
  website: string | null;
}

// --- Logistics API functions ---

export async function getLogistics(
  festivalId: string,
): Promise<LogisticsSummary> {
  return request<LogisticsSummary>(`/festivals/${festivalId}/logistics`);
}

export async function listHotels(
  festivalId: string,
): Promise<HotelWithDistances[]> {
  return request<HotelWithDistances[]>(
    `/festivals/${festivalId}/logistics/hotels`,
  );
}

export async function createHotel(
  festivalId: string,
  data: {
    name: string;
    address: string;
    lat: number;
    lng: number;
    place_id?: string;
    stars?: number;
    price_per_night?: number;
    rooms_available?: number;
    notes?: string;
  },
): Promise<HotelResponseItem> {
  return request<HotelResponseItem>(
    `/festivals/${festivalId}/logistics/hotels`,
    {
      method: "POST",
      body: JSON.stringify(data),
    },
  );
}

export async function updateHotel(
  festivalId: string,
  hotelId: string,
  data: {
    name: string;
    address: string;
    lat: number;
    lng: number;
    place_id?: string;
    stars?: number;
    price_per_night?: number;
    rooms_available?: number;
    notes?: string;
  },
): Promise<HotelResponseItem> {
  return request<HotelResponseItem>(
    `/festivals/${festivalId}/logistics/hotels/${hotelId}`,
    {
      method: "PUT",
      body: JSON.stringify(data),
    },
  );
}

export async function deleteHotel(
  festivalId: string,
  hotelId: string,
): Promise<void> {
  return request<void>(`/festivals/${festivalId}/logistics/hotels/${hotelId}`, {
    method: "DELETE",
  });
}

export async function discoverHotels(
  festivalId: string,
  params?: { radius?: number; min_rating?: number; max_price_level?: number },
): Promise<HotelSuggestion[]> {
  const searchParams = new URLSearchParams();
  if (params?.radius) searchParams.set("radius", String(params.radius));
  if (params?.min_rating)
    searchParams.set("min_rating", String(params.min_rating));
  if (params?.max_price_level !== undefined)
    searchParams.set("max_price_level", String(params.max_price_level));
  const qs = searchParams.toString();
  return request<HotelSuggestion[]>(
    `/festivals/${festivalId}/logistics/hotels/discover${qs ? `?${qs}` : ""}`,
  );
}

export async function createAssignment(
  festivalId: string,
  hotelId: string,
  data: {
    artist_name: string;
    rider_id?: string;
    check_in: string;
    check_out: string;
    room_type?: string;
    rooms_count?: number;
    notes?: string;
    arrival_datetime?: string;
    departure_datetime?: string;
    airport_iata?: string;
    arrival_flight?: string;
    departure_flight?: string;
  },
): Promise<AssignmentResponse> {
  return request<AssignmentResponse>(
    `/festivals/${festivalId}/logistics/hotels/${hotelId}/assign`,
    {
      method: "POST",
      body: JSON.stringify(data),
    },
  );
}

export async function updateAssignment(
  festivalId: string,
  assignmentId: string,
  data: {
    artist_name: string;
    rider_id?: string;
    check_in: string;
    check_out: string;
    room_type?: string;
    rooms_count?: number;
    notes?: string;
    arrival_datetime?: string;
    departure_datetime?: string;
    airport_iata?: string;
    arrival_flight?: string;
    departure_flight?: string;
  },
): Promise<AssignmentResponse> {
  return request<AssignmentResponse>(
    `/festivals/${festivalId}/logistics/assignments/${assignmentId}`,
    {
      method: "PUT",
      body: JSON.stringify(data),
    },
  );
}

export async function deleteAssignment(
  festivalId: string,
  assignmentId: string,
): Promise<void> {
  return request<void>(
    `/festivals/${festivalId}/logistics/assignments/${assignmentId}`,
    {
      method: "DELETE",
    },
  );
}

export async function listNearbyAirports(
  festivalId: string,
  radiusKm?: number,
): Promise<AirportWithDistance[]> {
  const qs = radiusKm ? `?radius_km=${radiusKm}` : "";
  return request<AirportWithDistance[]>(
    `/festivals/${festivalId}/logistics/airports${qs}`,
  );
}

export async function recalculateDistances(
  festivalId: string,
): Promise<{ status: string }> {
  return request<{ status: string }>(
    `/festivals/${festivalId}/logistics/recalculate`,
    {
      method: "POST",
    },
  );
}

// --- Preferred airports ---

export async function getPreferredAirports(
  festivalId: string,
): Promise<FestivalAirportResponse[]> {
  return request<FestivalAirportResponse[]>(
    `/festivals/${festivalId}/logistics/preferred-airports`,
  );
}

export async function addPreferredAirport(
  festivalId: string,
  airportIata: string,
): Promise<FestivalAirportResponse> {
  return request<FestivalAirportResponse>(
    `/festivals/${festivalId}/logistics/preferred-airports`,
    {
      method: "POST",
      body: JSON.stringify({ airport_iata: airportIata }),
    },
  );
}

export async function reorderPreferredAirports(
  festivalId: string,
  airportIatas: string[],
): Promise<FestivalAirportResponse[]> {
  return request<FestivalAirportResponse[]>(
    `/festivals/${festivalId}/logistics/preferred-airports/reorder`,
    {
      method: "PUT",
      body: JSON.stringify({ airport_iatas: airportIatas }),
    },
  );
}

export async function removePreferredAirport(
  festivalId: string,
  airportIata: string,
): Promise<void> {
  return request<void>(
    `/festivals/${festivalId}/logistics/preferred-airports/${airportIata}`,
    {
      method: "DELETE",
    },
  );
}

// --- Maps proxy ---

export async function mapsAutocomplete(
  q: string,
  types?: string,
): Promise<PlaceSuggestion[]> {
  const params = new URLSearchParams({ q });
  if (types) params.set("types", types);
  return request<PlaceSuggestion[]>(`/maps/autocomplete?${params.toString()}`);
}

export async function mapsPlaceDetails(placeId: string): Promise<PlaceDetails> {
  return request<PlaceDetails>(`/maps/place/${placeId}`);
}

// --- Artist Profiles ---

export interface ArtistProfileResponse {
  id: string;
  display_name: string;
  slug: string | null;
  email: string | null;
  phone: string | null;
  photo_url: string | null;
  user_id: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ArtistRosterResponse {
  profile: ArtistProfileResponse;
  role: string;
  invite_status: string;
  invite_email: string | null;
  festival_names: string[];
  profiles_merged?: boolean;
}

export interface ArtistSlotBrief {
  stage_name: string;
  day: string;
  start_time: string | null;
  end_time: string | null;
}

export interface ArtistHotelBrief {
  hotel_name: string;
  check_in: string | null;
  check_out: string | null;
  room_type: string | null;
  rooms_count: number;
}

export interface ArtistTransportEvent {
  event_type: string;
  time: string;
  from_location: string;
  to_location: string;
  duration_minutes: number | null;
}

export interface ArtistFestivalView {
  festival_id: string;
  festival_name: string;
  festival_dates: string;
  festival_location: string | null;
  festival_slug: string | null;
  organization_name: string;
  my_slots: ArtistSlotBrief[];
  my_hotel: ArtistHotelBrief | null;
  my_transport: ArtistTransportEvent[];
}

export interface ArtistDashboardResponse {
  profile: ArtistProfileResponse | null;
  upcoming: ArtistFestivalView[];
  past: ArtistFestivalView[];
}

export async function listOrgArtists(
  orgId: string,
): Promise<ArtistRosterResponse[]> {
  return request<ArtistRosterResponse[]>(`/organizations/${orgId}/artists`);
}

export async function createOrgArtist(
  orgId: string,
  data: {
    display_name: string;
    email?: string;
    phone?: string;
    role?: string;
    send_invite?: boolean;
  },
): Promise<ArtistRosterResponse> {
  return request<ArtistRosterResponse>(`/organizations/${orgId}/artists`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateOrgArtist(
  orgId: string,
  profileId: string,
  data: {
    display_name?: string;
    email?: string;
    phone?: string;
    role?: string;
  },
): Promise<ArtistRosterResponse> {
  return request<ArtistRosterResponse>(
    `/organizations/${orgId}/artists/${profileId}`,
    {
      method: "PATCH",
      body: JSON.stringify(data),
    },
  );
}

export async function removeOrgArtist(
  orgId: string,
  profileId: string,
): Promise<void> {
  return request<void>(`/organizations/${orgId}/artists/${profileId}`, {
    method: "DELETE",
  });
}

export async function inviteArtist(
  orgId: string,
  profileId: string,
  email: string,
): Promise<ArtistRosterResponse> {
  return request<ArtistRosterResponse>(
    `/organizations/${orgId}/artists/${profileId}/invite`,
    {
      method: "POST",
      body: JSON.stringify({ email }),
    },
  );
}

export async function acceptArtistInvite(
  token: string,
): Promise<ArtistRosterResponse> {
  return request<ArtistRosterResponse>(`/artist-invites/${token}/accept`, {
    method: "POST",
  });
}

// Artist dashboard

export async function getArtistDashboard(): Promise<ArtistDashboardResponse> {
  return request<ArtistDashboardResponse>("/me/artist-dashboard");
}

// ---------- Crew ----------

export type CrewRoleCategory = "production" | "technical" | "support";
export type CrewInviteStatus = "none" | "pending" | "active";

export interface CrewRole {
  id: string;
  organization_id: string;
  label: string;
  category: CrewRoleCategory;
  stage_required: boolean;
  color: string | null;
  is_system: boolean;
  is_archived: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface CrewMember {
  id: string;
  organization_id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  photo_url: string | null;
  default_role_id: string | null;
  default_role: CrewRole | null;
  user_id: string | null;
  invite_status: CrewInviteStatus;
  invite_email: string | null;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CrewMemberListItem {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  photo_url: string | null;
  default_role: CrewRole | null;
  invite_status: CrewInviteStatus;
  is_active: boolean;
  team_labels: string[];
  assignment_count: number;
}

export interface CrewTeam {
  id: string;
  organization_id: string;
  label: string;
  color: string | null;
  notes: string | null;
  member_count: number;
  created_at: string;
  updated_at: string;
}

export interface CrewAssignment {
  id: string;
  festival_id: string;
  crew_member_id: string;
  stage_id: string | null;
  /** The shift's production unit (crew team); null = generic production. */
  team_id: string | null;
  day: string;
  role_id: string;
  start_time: string | null;
  end_time: string | null;
  notes: string | null;
  crew_member_name: string;
  role_label: string;
  role_category: CrewRoleCategory;
  stage_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface CrewSheetPerson {
  assignment_id: string;
  crew_member_id: string;
  full_name: string;
  photo_url: string | null;
  phone: string | null;
  email: string | null;
  role_label: string;
  role_color: string | null;
  start_time: string | null;
  end_time: string | null;
  notes: string | null;
  /** Production unit of the shift — drives the by-production view/filter. */
  team_id: string | null;
  team_label: string | null;
  team_color: string | null;
  stage_name: string | null;
}

export interface CrewSheetStageSection {
  stage_id: string;
  stage_name: string;
  /** Venue row (location) vs performance stage — drives the aggregate
   * "venues"/"stages" section filters. */
  is_venue: boolean;
  needed: number;
  assigned: number;
  missing: number;
  people: CrewSheetPerson[];
}

export interface CrewSheet {
  festival_id: string;
  festival_name: string;
  day: string;
  production: CrewSheetPerson[];
  stages: CrewSheetStageSection[];
  support: CrewSheetPerson[];
}

export interface CrewGap {
  stage_id: string;
  stage_name: string;
  day: string;
  needed: number;
  assigned: number;
  missing: number;
}

export interface CrewConflict {
  crew_member_id: string;
  crew_member_name: string;
  day: string;
  assignments: {
    assignment_id: string;
    stage_name: string | null;
    start_time: string | null;
    end_time: string | null;
  }[];
}

export interface MyShiftEntry {
  festival_id: string;
  festival_name: string;
  festival_slug: string | null;
  crew_member_id: string;
  day: string;
  stage_id: string | null;
  stage_name: string | null;
  role_label: string;
  start_time: string | null;
  end_time: string | null;
  production_director_name: string | null;
  production_director_phone: string | null;
}

export interface BatchAssignRequest {
  crew_member_ids?: string[];
  team_id?: string;
  stage_id?: string | null;
  role_id: string;
  days?: string[];
  recurrence?: {
    type: "weekly";
    day_of_week: number;
    from: string;
    until: string;
    skip_dates?: string[];
  };
  start_time?: string;
  end_time?: string;
}

export interface BatchAssignResponse {
  created: number;
  skipped_duplicates: number;
  conflicts: {
    day: string;
    crew_member_id: string;
    crew_member_name: string;
    reason: string;
  }[];
  created_ids: string[];
}

// ---------- Crew — Roles ----------

export async function listCrewRoles(
  orgId: string,
  includeArchived = false,
): Promise<CrewRole[]> {
  const q = includeArchived ? "?include_archived=true" : "";
  return request<CrewRole[]>(`/organizations/${orgId}/crew/roles${q}`);
}

export async function createCrewRole(
  orgId: string,
  body: Partial<CrewRole>,
): Promise<CrewRole> {
  return request<CrewRole>(`/organizations/${orgId}/crew/roles`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function updateCrewRole(
  orgId: string,
  roleId: string,
  body: Partial<CrewRole>,
): Promise<CrewRole> {
  return request<CrewRole>(`/organizations/${orgId}/crew/roles/${roleId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function deleteCrewRole(
  orgId: string,
  roleId: string,
): Promise<void> {
  return request<void>(`/organizations/${orgId}/crew/roles/${roleId}`, {
    method: "DELETE",
  });
}

export async function archiveCrewRole(
  orgId: string,
  roleId: string,
): Promise<CrewRole> {
  return request<CrewRole>(
    `/organizations/${orgId}/crew/roles/${roleId}/archive`,
    { method: "PATCH" },
  );
}

export async function unarchiveCrewRole(
  orgId: string,
  roleId: string,
): Promise<CrewRole> {
  return request<CrewRole>(
    `/organizations/${orgId}/crew/roles/${roleId}/unarchive`,
    { method: "PATCH" },
  );
}

// ---------- Crew — Members ----------

export async function listCrewMembers(
  orgId: string,
  opts?: {
    includeInactive?: boolean;
    roleId?: string;
    teamId?: string;
    inviteStatus?: string;
  },
): Promise<CrewMemberListItem[]> {
  const params = new URLSearchParams();
  if (opts?.includeInactive) params.set("include_inactive", "true");
  if (opts?.roleId) params.set("role_id", opts.roleId);
  if (opts?.teamId) params.set("team_id", opts.teamId);
  if (opts?.inviteStatus) params.set("invite_status", opts.inviteStatus);
  const qs = params.toString();
  return request<CrewMemberListItem[]>(
    `/organizations/${orgId}/crew/members${qs ? "?" + qs : ""}`,
  );
}

export async function createCrewMember(
  orgId: string,
  body: Partial<CrewMember>,
): Promise<CrewMember> {
  return request<CrewMember>(`/organizations/${orgId}/crew/members`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function getCrewMember(
  orgId: string,
  memberId: string,
): Promise<CrewMember> {
  return request<CrewMember>(
    `/organizations/${orgId}/crew/members/${memberId}`,
  );
}

export async function updateCrewMember(
  orgId: string,
  memberId: string,
  body: Partial<CrewMember>,
): Promise<CrewMember> {
  return request<CrewMember>(
    `/organizations/${orgId}/crew/members/${memberId}`,
    { method: "PATCH", body: JSON.stringify(body) },
  );
}

export async function deleteCrewMember(
  orgId: string,
  memberId: string,
): Promise<void> {
  return request<void>(`/organizations/${orgId}/crew/members/${memberId}`, {
    method: "DELETE",
  });
}

export async function deactivateCrewMember(
  orgId: string,
  memberId: string,
): Promise<CrewMember> {
  return request<CrewMember>(
    `/organizations/${orgId}/crew/members/${memberId}/deactivate`,
    { method: "PATCH" },
  );
}

export async function reactivateCrewMember(
  orgId: string,
  memberId: string,
): Promise<CrewMember> {
  return request<CrewMember>(
    `/organizations/${orgId}/crew/members/${memberId}/reactivate`,
    { method: "PATCH" },
  );
}

export async function sendCrewInvite(
  orgId: string,
  memberId: string,
  inviteEmail: string,
): Promise<CrewMember> {
  return request<CrewMember>(
    `/organizations/${orgId}/crew/members/${memberId}/invite`,
    {
      method: "POST",
      body: JSON.stringify({ invite_email: inviteEmail }),
    },
  );
}

export async function revokeCrewInvite(
  orgId: string,
  memberId: string,
): Promise<void> {
  return request<void>(
    `/organizations/${orgId}/crew/members/${memberId}/invite/revoke`,
    { method: "POST" },
  );
}

export async function searchCrewMembers(
  orgId: string,
  q: string,
): Promise<CrewMemberListItem[]> {
  return request<CrewMemberListItem[]>(
    `/organizations/${orgId}/crew/members/search?q=${encodeURIComponent(q)}`,
  );
}

// Photo upload — multipart/form-data, mirrors `uploadRider` pattern above.
export async function uploadCrewPhoto(
  orgId: string,
  memberId: string,
  file: File,
): Promise<CrewMember> {
  const token = useAuthStore.getState().accessToken;
  const formData = new FormData();
  formData.append("file", file);

  const headers: Record<string, string> = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(
    `${API_BASE}/organizations/${orgId}/crew/members/${memberId}/photo`,
    {
      method: "POST",
      body: formData,
      headers,
    },
  );

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || "Upload failed");
  }

  return response.json();
}

// ---------- Crew — Teams ----------

export async function listCrewTeams(orgId: string): Promise<CrewTeam[]> {
  return request<CrewTeam[]>(`/organizations/${orgId}/crew/teams`);
}

export async function createCrewTeam(
  orgId: string,
  body: {
    label: string;
    color?: string;
    notes?: string;
    member_ids?: string[];
  },
): Promise<CrewTeam> {
  return request<CrewTeam>(`/organizations/${orgId}/crew/teams`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function updateCrewTeam(
  orgId: string,
  teamId: string,
  body: Partial<CrewTeam>,
): Promise<CrewTeam> {
  return request<CrewTeam>(`/organizations/${orgId}/crew/teams/${teamId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function deleteCrewTeam(
  orgId: string,
  teamId: string,
): Promise<void> {
  return request<void>(`/organizations/${orgId}/crew/teams/${teamId}`, {
    method: "DELETE",
  });
}

export async function addCrewTeamMembers(
  orgId: string,
  teamId: string,
  memberIds: string[],
): Promise<void> {
  return request<void>(`/organizations/${orgId}/crew/teams/${teamId}/members`, {
    method: "POST",
    body: JSON.stringify({ member_ids: memberIds }),
  });
}

export async function removeCrewTeamMember(
  orgId: string,
  teamId: string,
  memberId: string,
): Promise<void> {
  return request<void>(
    `/organizations/${orgId}/crew/teams/${teamId}/members/${memberId}`,
    { method: "DELETE" },
  );
}

// ---------- Crew — Assignments ----------

export interface CrewCalendarDays {
  days: string[];
}

export async function getCrewCalendarDays(
  festivalId: string,
): Promise<string[]> {
  const res = await request<CrewCalendarDays>(
    `/festivals/${festivalId}/crew/calendar-days`,
  );
  return res.days;
}

export async function listCrewAssignments(
  festivalId: string,
  opts?: { day?: string; stageId?: string },
): Promise<CrewAssignment[]> {
  const p = new URLSearchParams();
  if (opts?.day) p.set("day", opts.day);
  if (opts?.stageId) p.set("stage_id", opts.stageId);
  const qs = p.toString();
  return request<CrewAssignment[]>(
    `/festivals/${festivalId}/crew/assignments${qs ? "?" + qs : ""}`,
  );
}

export async function createCrewAssignment(
  festivalId: string,
  body: Partial<CrewAssignment>,
): Promise<CrewAssignment> {
  return request<CrewAssignment>(`/festivals/${festivalId}/crew/assignments`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function batchCreateCrewAssignments(
  festivalId: string,
  body: BatchAssignRequest,
): Promise<BatchAssignResponse> {
  return request<BatchAssignResponse>(
    `/festivals/${festivalId}/crew/assignments/batch`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export async function updateCrewAssignment(
  festivalId: string,
  assignmentId: string,
  body: Partial<CrewAssignment>,
): Promise<CrewAssignment> {
  return request<CrewAssignment>(
    `/festivals/${festivalId}/crew/assignments/${assignmentId}`,
    { method: "PATCH", body: JSON.stringify(body) },
  );
}

export async function deleteCrewAssignment(
  festivalId: string,
  assignmentId: string,
): Promise<void> {
  return request<void>(
    `/festivals/${festivalId}/crew/assignments/${assignmentId}`,
    { method: "DELETE" },
  );
}

// ---------- Crew — Sheet / Gaps / Conflicts / Dashboard ----------

export async function getCrewSheet(
  festivalId: string,
  day: string,
): Promise<CrewSheet> {
  return request<CrewSheet>(`/festivals/${festivalId}/crew/sheet?day=${day}`);
}

export async function downloadAuthed(
  url: string,
  filename: string,
  errorFallback = "Download failed",
): Promise<void> {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    // Exports must always be fresh: a cached response serves yesterday's data.
    cache: "no-store",
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(error.detail || errorFallback);
  }
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Release the blob on the next tick so the browser has time to start the download
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
}

export async function downloadCrewPersonalSchedulePdf(
  festivalId: string,
  memberId: string,
): Promise<void> {
  await downloadAuthed(
    `${API_BASE}/festivals/${festivalId}/crew/members/${memberId}/schedule.pdf`,
    `crew-personal-schedule.pdf`,
    "Failed to download personal schedule",
  );
}

/** Print the crew day sheet exactly as filtered on screen:
 *  `section` = "production" | "support" | "stage:<id>" (omit for all). */
export interface CrewPrintOptions {
  paper?: "a4" | "a3";
  orientation?: "landscape" | "portrait";
  margins?: "narrow" | "normal" | "wide";
}

export async function downloadCrewSheetPdf(
  festivalId: string,
  day: string,
  section?: string,
  locale = "it",
  options?: CrewPrintOptions,
): Promise<void> {
  const params = new URLSearchParams({ day, locale });
  if (section) params.set("section", section);
  if (options?.paper) params.set("paper", options.paper);
  if (options?.orientation) params.set("orientation", options.orientation);
  if (options?.margins) params.set("margins", options.margins);
  await downloadAuthed(
    `${API_BASE}/festivals/${festivalId}/crew/sheet.pdf?${params.toString()}`,
    `crew-sheet-${day}.pdf`,
    "Failed to download crew sheet",
  );
}

export async function getCrewGaps(festivalId: string): Promise<CrewGap[]> {
  return request<CrewGap[]>(`/festivals/${festivalId}/crew/gaps`);
}

export async function getCrewConflicts(
  festivalId: string,
): Promise<CrewConflict[]> {
  return request<CrewConflict[]>(`/festivals/${festivalId}/crew/conflicts`);
}

export async function getMyCrewDashboard(): Promise<MyShiftEntry[]> {
  return request<MyShiftEntry[]>("/me/crew-dashboard");
}

// ---------- Crew — Invite accept ----------

export async function acceptCrewInvite(token: string): Promise<{
  crew_member_id: string;
  organization_id: string;
  status: "accepted" | "already_active";
}> {
  return request<{
    crew_member_id: string;
    organization_id: string;
    status: "accepted" | "already_active";
  }>("/crew-invites/accept", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

// ============================================================================
// Artist Portal v1 (Sprint 1 + Sprint 2 endpoints)
// ============================================================================

// ---- Types ----

export type PortalFestivalMeta = {
  id: string;
  name: string;
  slug: string | null;
  start_date: string;
  end_date: string;
  location: string | null;
  venue_address: string | null;
  timezone: string;
};

export type PortalMySlot = {
  stage_name: string;
  day: string;
  start_time: string | null;
  end_time: string | null;
};

export type PortalMyHotel = {
  hotel_name: string;
  check_in: string | null;
  check_out: string | null;
  room_type: string | null;
};

export type PortalTransportSummary = {
  has_pickup: boolean;
  has_dropoff: boolean;
  first_event_date: string | null;
  last_event_date: string | null;
};

export type PortalLineupRow = {
  artist_name: string;
  stage_name: string;
  day: string;
  start_time: string | null;
  end_time: string | null;
};

export type PortalEmergencyContactBrief = {
  id: string;
  name: string;
  role: string;
  phone: string | null;
  email: string | null;
};

export type PortalOverviewResponse = {
  festival: PortalFestivalMeta;
  my_slots: PortalMySlot[];
  my_hotel: PortalMyHotel | null;
  transport_summary: PortalTransportSummary;
  emergency_contacts: PortalEmergencyContactBrief[];
  lineup_lite: PortalLineupRow[];
  counts: { documents: number; schedule_events: number };
};

export type PortalItineraryEvent = {
  source:
    | "derived_slot"
    | "derived_hotel"
    | "derived_transport"
    | "schedule_event";
  title: string;
  start: string;
  end: string;
  location: string | null;
  category: string | null;
  notes: string | null;
  overlap_with: string[] | null;
};

export type PortalItineraryDay = {
  day: string;
  events: PortalItineraryEvent[];
};

export type EmergencyContact = {
  id: string;
  festival_id: string;
  name: string;
  role: string;
  phone: string | null;
  email: string | null;
  notes: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export type EmergencyContactCreate = {
  name: string;
  role: string;
  phone?: string | null;
  email?: string | null;
  notes?: string | null;
  sort_order?: number;
};

export type ScheduleEvent = {
  id: string;
  festival_id: string;
  artist_profile_id: string | null;
  /** Festival lineup slot this event follows, even when no roster profile exists. */
  slot_id: string | null;
  /** Canonical festival stage; null when `location` is a custom place. */
  stage_id: string | null;
  /** Free-text artist name for events whose artist has no org profile. */
  artist_label?: string | null;
  day: string;
  start_datetime: string;
  end_datetime: string;
  category: string;
  title: string;
  location: string | null;
  notes: string | null;
  created_by_role: "org" | "artist";
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
};

export type ScheduleEventCreate = {
  artist_profile_id?: string | null;
  slot_id?: string | null;
  stage_id?: string | null;
  /** Free-text artist name — required when artist_profile_id is absent. */
  artist_label?: string | null;
  day: string;
  start_datetime: string;
  end_datetime: string;
  category: string;
  title: string;
  location?: string | null;
  notes?: string | null;
};

export type ArtistDocument = {
  id: string;
  festival_id: string;
  artist_profile_id: string;
  organization_id: string;
  category: string;
  title: string;
  description: string | null;
  file_name: string;
  file_size_bytes: number;
  content_type: string;
  uploaded_by_user_id: string | null;
  created_at: string;
  updated_at: string;
};

export type ArtistDocumentDownload = {
  signed_url: string;
  expires_at: string;
};

// ---- Artist Portal — artist-side reads ----

export async function getPortalOverview(
  festivalId: string,
): Promise<PortalOverviewResponse> {
  return request<PortalOverviewResponse>(`/me/festivals/${festivalId}/portal`);
}

export async function getPortalLineup(
  festivalId: string,
): Promise<PortalLineupRow[]> {
  return request<PortalLineupRow[]>(`/me/festivals/${festivalId}/lineup`);
}

export async function getPortalItinerary(
  festivalId: string,
): Promise<PortalItineraryDay[]> {
  return request<PortalItineraryDay[]>(`/me/festivals/${festivalId}/itinerary`);
}

export async function getPortalDocuments(
  festivalId: string,
): Promise<ArtistDocument[]> {
  return request<ArtistDocument[]>(`/me/festivals/${festivalId}/documents`);
}

export async function downloadPortalDocument(
  festivalId: string,
  docId: string,
): Promise<ArtistDocumentDownload> {
  return request<ArtistDocumentDownload>(
    `/me/festivals/${festivalId}/documents/${docId}/download`,
  );
}

// ---- Artist Portal — artist-side schedule events ----

export async function listMyScheduleEvents(
  festivalId: string,
): Promise<ScheduleEvent[]> {
  return request<ScheduleEvent[]>(
    `/me/festivals/${festivalId}/schedule-events`,
  );
}

export async function createMyScheduleEvent(
  festivalId: string,
  body: ScheduleEventCreate,
): Promise<ScheduleEvent> {
  return request<ScheduleEvent>(`/me/festivals/${festivalId}/schedule-events`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function updateMyScheduleEvent(
  festivalId: string,
  eid: string,
  body: Partial<ScheduleEventCreate>,
): Promise<ScheduleEvent> {
  return request<ScheduleEvent>(
    `/me/festivals/${festivalId}/schedule-events/${eid}`,
    {
      method: "PATCH",
      body: JSON.stringify(body),
    },
  );
}

export async function deleteMyScheduleEvent(
  festivalId: string,
  eid: string,
): Promise<void> {
  await request<void>(`/me/festivals/${festivalId}/schedule-events/${eid}`, {
    method: "DELETE",
  });
}

// ---- Emergency contacts (org-side CRUD + reorder) ----

export async function listEmergencyContacts(
  festivalId: string,
): Promise<EmergencyContact[]> {
  return request<EmergencyContact[]>(
    `/festivals/${festivalId}/emergency-contacts`,
  );
}

export async function createEmergencyContact(
  festivalId: string,
  body: EmergencyContactCreate,
): Promise<EmergencyContact> {
  return request<EmergencyContact>(
    `/festivals/${festivalId}/emergency-contacts`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}

export async function updateEmergencyContact(
  festivalId: string,
  eid: string,
  body: Partial<EmergencyContactCreate>,
): Promise<EmergencyContact> {
  return request<EmergencyContact>(
    `/festivals/${festivalId}/emergency-contacts/${eid}`,
    {
      method: "PATCH",
      body: JSON.stringify(body),
    },
  );
}

export async function deleteEmergencyContact(
  festivalId: string,
  eid: string,
): Promise<void> {
  await request<void>(`/festivals/${festivalId}/emergency-contacts/${eid}`, {
    method: "DELETE",
  });
}

export async function reorderEmergencyContacts(
  festivalId: string,
  items: { id: string; sort_order: number }[],
): Promise<EmergencyContact[]> {
  return request<EmergencyContact[]>(
    `/festivals/${festivalId}/emergency-contacts/reorder`,
    {
      method: "PATCH",
      body: JSON.stringify(items),
    },
  );
}

// ---- Schedule events (org-side) ----

export async function listOrgScheduleEvents(
  festivalId: string,
  params: { artist_profile_id?: string; slot_id?: string; day?: string } = {},
): Promise<ScheduleEvent[]> {
  const filtered = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v != null),
  ) as Record<string, string>;
  const qs = new URLSearchParams(filtered).toString();
  return request<ScheduleEvent[]>(
    `/festivals/${festivalId}/schedule-events${qs ? `?${qs}` : ""}`,
  );
}

export async function createOrgScheduleEvent(
  festivalId: string,
  body: ScheduleEventCreate,
): Promise<ScheduleEvent> {
  return request<ScheduleEvent>(`/festivals/${festivalId}/schedule-events`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function updateOrgScheduleEvent(
  festivalId: string,
  eid: string,
  body: Partial<ScheduleEventCreate>,
): Promise<ScheduleEvent> {
  return request<ScheduleEvent>(
    `/festivals/${festivalId}/schedule-events/${eid}`,
    {
      method: "PATCH",
      body: JSON.stringify(body),
    },
  );
}

export async function deleteOrgScheduleEvent(
  festivalId: string,
  eid: string,
): Promise<void> {
  await request<void>(`/festivals/${festivalId}/schedule-events/${eid}`, {
    method: "DELETE",
  });
}

// ---- Artist documents (org-side) ----

export async function listArtistDocumentsOrg(
  festivalId: string,
  profileId: string,
): Promise<ArtistDocument[]> {
  return request<ArtistDocument[]>(
    `/festivals/${festivalId}/artists/${profileId}/documents`,
  );
}

// Multipart upload — bypasses request<T> because that helper sets
// Content-Type: application/json and would break the multipart boundary.
// Mirrors the uploadRider / uploadCrewPhoto pattern higher up in this file.
export async function uploadArtistDocument(
  festivalId: string,
  profileId: string,
  file: File,
  metadata: { category: string; title: string; description?: string },
): Promise<ArtistDocument> {
  const token = useAuthStore.getState().accessToken;
  const fd = new FormData();
  fd.append("file", file);
  fd.append("category", metadata.category);
  fd.append("title", metadata.title);
  if (metadata.description) fd.append("description", metadata.description);

  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const response = await fetch(
    `${API_BASE}/festivals/${festivalId}/artists/${profileId}/documents`,
    { method: "POST", body: fd, headers },
  );
  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || "Upload failed");
  }
  return response.json();
}

export async function updateArtistDocumentOrg(
  festivalId: string,
  profileId: string,
  docId: string,
  body: { category?: string; title?: string; description?: string | null },
): Promise<ArtistDocument> {
  return request<ArtistDocument>(
    `/festivals/${festivalId}/artists/${profileId}/documents/${docId}`,
    { method: "PATCH", body: JSON.stringify(body) },
  );
}

export async function deleteArtistDocumentOrg(
  festivalId: string,
  profileId: string,
  docId: string,
): Promise<void> {
  await request<void>(
    `/festivals/${festivalId}/artists/${profileId}/documents/${docId}`,
    { method: "DELETE" },
  );
}

export async function downloadArtistDocumentOrg(
  festivalId: string,
  profileId: string,
  docId: string,
): Promise<ArtistDocumentDownload> {
  return request<ArtistDocumentDownload>(
    `/festivals/${festivalId}/artists/${profileId}/documents/${docId}/download`,
  );
}

// ---- Onboarding (welcome tour + progress) ----

export async function getProductTour(userId: string): Promise<import("@/lib/productTour").ProductTourState> {
  return request("/me/product-tour", { expectedUserId: userId });
}

export async function patchProductTour(userId: string, patch: import("@/lib/productTour").ProductTourPatch): Promise<import("@/lib/productTour").ProductTourState> {
  return request("/me/product-tour", { method: "PATCH", body: JSON.stringify(patch), expectedUserId: userId });
}

export interface OnboardingProgress {
  upload_first_rider: boolean;
  create_first_festival: boolean;
  approve_assign_rider: boolean;
  publish_timetable: boolean;
  expand_team: boolean;
}

export async function getOnboardingProgress(): Promise<OnboardingProgress> {
  return request<OnboardingProgress>("/me/onboarding-progress");
}

export async function postWelcomeShown(reset = false): Promise<void> {
  await request<void>("/me/welcome-shown", {
    method: "POST",
    body: JSON.stringify({ reset }),
  });
}

export async function postCelebrationAcked(): Promise<void> {
  await request<void>("/me/celebration-acked", { method: "POST" });
}

// ---- AI Assistant (festival intelligence chat) ----

/** Display snapshot of a PDP entry inside an assistant proposal card. */
export interface PlanProposalSnapshot {
  day: string;
  title: string;
  type: string;
  status: string;
  time: string;
  team: string | null;
  area: string | null;
  headcount: number | null;
  owner: string | null;
  notes: string | null;
}

/** A pending PDP change proposed by the assistant — applied only after the
 *  user confirms the card (via the normal production-plan endpoints). */
export interface PlanChangeProposal {
  /** Discriminator; absent on plan proposals stored before crew support. */
  kind?: "plan";
  id: string;
  action: "create" | "update" | "delete";
  entry_id: string | null;
  day: string;
  changes: (Partial<ProductionEntryInput> & { title?: string }) | null;
  before: PlanProposalSnapshot | null;
  after: PlanProposalSnapshot | null;
}

/** One crew shift operation inside a (possibly bulk) crew proposal. Display
 *  fields show the AFTER state; `changes` is the PATCH body for updates. */
export interface CrewShiftProposalItem {
  action: "create" | "update" | "delete";
  assignment_id: string | null;
  member: string;
  member_id: string | null;
  role: string | null;
  role_id: string | null;
  stage: string | null;
  stage_id: string | null;
  /** Production unit label + id the shift is tagged with. */
  production: string | null;
  team_id: string | null;
  day: string;
  start: string | null;
  end: string | null;
  notes: string | null;
  changes: Partial<{
    stage_id: string | null;
    team_id: string | null;
    day: string;
    start_time: string | null;
    end_time: string | null;
    role_id: string;
    notes: string | null;
  }> | null;
  before: {
    stage: string | null;
    day: string;
    start: string | null;
    end: string | null;
    role: string | null;
    production?: string | null;
  } | null;
  /** Non-blocking heads-up, e.g. "già assegnato quel giorno su: Main Stage". */
  warning?: string | null;
}

/** A pending crew shift change-set (1..N shifts) — one card, one confirm,
 *  applied through the normal crew assignment endpoints. */
export interface CrewChangeProposal {
  kind: "crew";
  id: string;
  day: string | null;
  summary: string;
  items: CrewShiftProposalItem[];
}

export type AssistantProposal = PlanChangeProposal | CrewChangeProposal;

export type PlanProposalResolution = "applied" | "rejected" | "failed";

export type StoredAssistantProposal = AssistantProposal & {
  resolution?: PlanProposalResolution;
  resolution_error?: string;
};

/** @deprecated kept as an alias for the pre-crew name. */
export type StoredPlanProposal = StoredAssistantProposal;

export interface AssistantChatMessage {
  role: "user" | "assistant";
  content: string;
  /** Store-only: proposal cards attached to an assistant message. Stripped
   *  before the history is re-sent to the API (content carries a text note
   *  with each proposal's outcome instead). */
  proposals?: StoredAssistantProposal[];
}

export interface AssistantReply {
  reply: string;
  input_tokens: number;
  output_tokens: number;
  tool_calls: number;
  monthly_used: number;
  monthly_limit: number | null;
  proposals?: PlanChangeProposal[];
}

/** The API accepts only {role, content} — store extras (proposal cards) stay
 *  client-side. */
function _apiMessages(
  messages: AssistantChatMessage[],
): { role: "user" | "assistant"; content: string }[] {
  return messages.map(({ role, content }) => ({ role, content }));
}

export async function sendAssistantMessage(
  festivalId: string,
  messages: AssistantChatMessage[],
): Promise<AssistantReply> {
  return request<AssistantReply>(`/festivals/${festivalId}/assistant/messages`, {
    method: "POST",
    body: JSON.stringify({ messages: _apiMessages(messages) }),
    // The agent loop can run several tool round-trips — well beyond the 15s default
    timeoutMs: 120_000,
  });
}

// ---- Admin: AI access flags ----

export interface AiAccessRow {
  user_id: string;
  email: string;
  name: string | null;
  ai_enabled: boolean;
  ai_monthly_limit: number | null;
  messages_this_month: number;
  tokens_this_month: number;
}

export async function updateAiAccess(
  userId: string,
  body: { ai_enabled: boolean; ai_monthly_limit: number | null },
): Promise<AiAccessRow> {
  return request<AiAccessRow>(`/admin/ai-access/${userId}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export interface AssistantStreamCallbacks {
  onTool?: (name: string) => void;
  onText?: (text: string) => void;
  onProposal?: (proposal: PlanChangeProposal) => void;
}

export async function streamAssistantMessage(
  festivalId: string,
  messages: AssistantChatMessage[],
  callbacks: AssistantStreamCallbacks = {},
): Promise<AssistantReply> {
  const doFetch = async (): Promise<Response> => {
    const token = useAuthStore.getState().accessToken;
    return fetch(`${API_BASE}/festivals/${festivalId}/assistant/messages/stream`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ messages: _apiMessages(messages) }),
    });
  };

  let response = await doFetch();
  if (response.status === 401) {
    const refreshed = await useAuthStore.getState().refresh();
    if (refreshed) response = await doFetch();
  }
  if (!response.ok || !response.body) {
    const error = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    throw new Error(
      typeof error.detail === "string" ? error.detail : "Request failed",
    );
  }

  // Parse SSE frames ("data: {json}\n\n") from the streaming body
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let done: AssistantReply | null = null;

  for (;;) {
    const { value, done: eof } = await reader.read();
    if (eof) break;
    buffer += decoder.decode(value, { stream: true });
    const frames = buffer.split("\n\n");
    buffer = frames.pop() ?? "";
    for (const frame of frames) {
      const line = frame.split("\n").find((l) => l.startsWith("data: "));
      if (!line) continue;
      const event = JSON.parse(line.slice(6)) as {
        type: string;
        name?: string;
        text?: string;
        detail?: string;
        proposal?: PlanChangeProposal;
      } & Partial<AssistantReply>;
      if (event.type === "tool" && event.name) {
        callbacks.onTool?.(event.name);
      } else if (event.type === "text" && event.text) {
        callbacks.onText?.(event.text);
      } else if (event.type === "proposal" && event.proposal) {
        callbacks.onProposal?.(event.proposal);
      } else if (event.type === "error") {
        throw new Error(event.detail || "Assistant error");
      } else if (event.type === "done") {
        done = event as unknown as AssistantReply;
      }
    }
  }
  if (!done) throw new Error("Assistant stream ended unexpectedly");
  return done;
}

/** Convert an assistant-produced CSV table into an xlsx download (no storage:
 *  the table text is posted back and the workbook streams as an attachment). */
export async function assistantTableToXlsx(
  festivalId: string,
  title: string,
  csv: string,
): Promise<void> {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(
    `${API_BASE}/festivals/${festivalId}/assistant/table-to-xlsx`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ title, csv }),
    },
  );
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(
      typeof error.detail === "string" ? error.detail : "Export failed",
    );
  }
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = `${title}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
}

// --- Production run-of-show (PDP) ---

export type ProductionEntryType =
  | "work"
  | "call"
  | "vehicle"
  | "meal"
  | "milestone"
  | "hold"
  | "other";
export type ProductionEntryStatus = "planned" | "confirmed" | "done";
export type ProductionAreaKind = "stage" | "technical" | "public" | "venue";

export interface ProductionTeam {
  id: string;
  name: string;
  palette_index: number;
  contact_name: string | null;
  contact_phone: string | null;
  notes: string | null;
  sort_order: number;
}

export interface ProductionArea {
  id: string;
  name: string;
  kind: ProductionAreaKind;
  stage_id: string | null;
  sort_order: number;
}

export interface ProductionEntry {
  id: string;
  day: string;
  start_datetime: string | null;
  end_datetime: string | null;
  start_label: string | null;
  end_label: string | null;
  start_day_offset: number;
  end_day_offset: number;
  time_tbc: boolean;
  entry_type: ProductionEntryType;
  title: string;
  team_id: string | null;
  area_id: string | null;
  headcount: number | null;
  owner_name: string | null;
  status: ProductionEntryStatus;
  notes: string | null;
  sort_order: number;
}

export interface ProductionPlanResponse {
  teams: ProductionTeam[];
  areas: ProductionArea[];
  days: string[];
  entries: ProductionEntry[];
}

export interface ProductionEntryInput {
  day: string;
  start?: string | null;
  end?: string | null;
  start_next_day?: boolean;
  end_next_day?: boolean;
  time_tbc?: boolean;
  entry_type?: ProductionEntryType;
  title?: string;
  team_id?: string | null;
  area_id?: string | null;
  headcount?: number | null;
  owner_name?: string | null;
  status?: ProductionEntryStatus;
  notes?: string | null;
  sort_order?: number;
}

function _pdpBase(festivalId: string): string {
  return `/festivals/${festivalId}/production-plan`;
}

export async function getProductionPlan(
  festivalId: string,
  day?: string,
): Promise<ProductionPlanResponse> {
  const query = day ? `?day=${day}` : "";
  return request<ProductionPlanResponse>(`${_pdpBase(festivalId)}${query}`);
}

export async function createProductionEntry(
  festivalId: string,
  data: ProductionEntryInput & { title: string },
): Promise<ProductionEntry> {
  return request<ProductionEntry>(`${_pdpBase(festivalId)}/entries`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateProductionEntry(
  festivalId: string,
  entryId: string,
  data: Partial<ProductionEntryInput>,
): Promise<ProductionEntry> {
  return request<ProductionEntry>(`${_pdpBase(festivalId)}/entries/${entryId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteProductionEntry(
  festivalId: string,
  entryId: string,
): Promise<void> {
  await request<void>(`${_pdpBase(festivalId)}/entries/${entryId}`, {
    method: "DELETE",
  });
}

export async function createProductionTeam(
  festivalId: string,
  data: { name: string; contact_name?: string | null; contact_phone?: string | null; notes?: string | null },
): Promise<ProductionTeam> {
  return request<ProductionTeam>(`${_pdpBase(festivalId)}/teams`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateProductionTeam(
  festivalId: string,
  teamId: string,
  data: Partial<{ name: string; contact_name: string | null; contact_phone: string | null; notes: string | null; sort_order: number }>,
): Promise<ProductionTeam> {
  return request<ProductionTeam>(`${_pdpBase(festivalId)}/teams/${teamId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteProductionTeam(
  festivalId: string,
  teamId: string,
): Promise<void> {
  await request<void>(`${_pdpBase(festivalId)}/teams/${teamId}`, {
    method: "DELETE",
  });
}

export async function createProductionArea(
  festivalId: string,
  data: { name: string; kind?: ProductionAreaKind },
): Promise<ProductionArea> {
  return request<ProductionArea>(`${_pdpBase(festivalId)}/areas`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateProductionArea(
  festivalId: string,
  areaId: string,
  data: Partial<{ name: string; kind: ProductionAreaKind; sort_order: number }>,
): Promise<ProductionArea> {
  return request<ProductionArea>(`${_pdpBase(festivalId)}/areas/${areaId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteProductionArea(
  festivalId: string,
  areaId: string,
): Promise<void> {
  await request<void>(`${_pdpBase(festivalId)}/areas/${areaId}`, {
    method: "DELETE",
  });
}

export async function seedProductionAreas(
  festivalId: string,
): Promise<ProductionArea[]> {
  return request<ProductionArea[]>(
    `${_pdpBase(festivalId)}/areas/seed-from-stages`,
    { method: "POST" },
  );
}

export async function shiftProductionEntries(
  festivalId: string,
  data: {
    day: string;
    delta_minutes: number;
    from_time?: string | null;
    team_id?: string | null;
    area_id?: string | null;
  },
): Promise<{ shifted: number }> {
  return request<{ shifted: number }>(`${_pdpBase(festivalId)}/shift`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function copyProductionDay(
  festivalId: string,
  sourceDay: string,
  toDate: string,
): Promise<{ copied: number }> {
  return request<{ copied: number }>(
    `${_pdpBase(festivalId)}/days/${sourceDay}/copy`,
    { method: "POST", body: JSON.stringify({ to_date: toDate }) },
  );
}

export async function downloadProductionPlanPdf(
  festivalId: string,
  day: string,
  locale: "en" | "it" = "en",
): Promise<void> {
  await downloadAuthed(
    `${API_BASE}${_pdpBase(festivalId)}/export/pdf?day=${day}&locale=${locale}`,
    `pdp-${day}.pdf`,
    "Failed to download the day PDF",
  );
}

export async function downloadProductionPlanGanttPdf(
  festivalId: string,
  day: string,
  paper: "a3" | "a2",
  group: "team" | "area",
  locale: "en" | "it" = "en",
): Promise<void> {
  await downloadAuthed(
    `${API_BASE}${_pdpBase(festivalId)}/export/gantt-pdf?day=${day}&paper=${paper}&group=${group}&locale=${locale}`,
    `pdp-gantt-${day}-${paper}.pdf`,
    "Failed to download the Gantt PDF",
  );
}

export async function downloadProductionPlanXlsx(
  festivalId: string,
  locale: "en" | "it" = "en",
): Promise<void> {
  await downloadAuthed(
    `${API_BASE}${_pdpBase(festivalId)}/export/xlsx?locale=${locale}`,
    `pdp.xlsx`,
    "Failed to download the PDP workbook",
  );
}

export async function downloadProductionPlanCsv(
  festivalId: string,
  locale: "en" | "it" = "en",
): Promise<void> {
  await downloadAuthed(
    `${API_BASE}${_pdpBase(festivalId)}/export/csv?locale=${locale}`,
    `production-plan.csv`,
    "Failed to download the production plan CSV",
  );
}

export async function deleteProductionDay(
  festivalId: string,
  day: string,
): Promise<{ deleted: number }> {
  return request<{ deleted: number }>(
    `${_pdpBase(festivalId)}/days/${day}`,
    { method: "DELETE" },
  );
}

export async function resetProductionPlan(
  festivalId: string,
  includeRegistries: boolean,
): Promise<{ entries: number; teams: number; areas: number }> {
  return request<{ entries: number; teams: number; areas: number }>(
    `${_pdpBase(festivalId)}?include_registries=${includeRegistries}`,
    { method: "DELETE" },
  );
}

// --- PDP Excel import -------------------------------------------------------

export interface PdpImportPreviewRow {
  row_index: number;
  include: boolean;
  day: string | null;
  start_time: string | null;
  end_time: string | null;
  start_next_day: boolean;
  end_next_day: boolean;
  time_tbc: boolean;
  entry_type: 'work' | 'call' | 'vehicle' | 'meal' | 'milestone' | 'hold' | 'other';
  title: string;
  team_name: string | null;
  team_all: boolean;
  chi_full: string | null;
  area_name: string | null;
  headcount: number | null;
  owner_name: string | null;
  notes: string | null;
  confidence: number;
  flags: string[];
}

export interface PdpImportSkippedSheet {
  sheet: string;
  reason: string;
}

export interface PdpImportRecord {
  id: string;
  status: 'parsing' | 'needs_review' | 'committed' | 'discarded' | 'failed';
  source_filename: string;
  preview: PdpImportPreviewRow[];
  stats: Record<string, unknown> & {
    sheets_parsed?: number;
    sheets_skipped?: PdpImportSkippedSheet[];
  };
  error: string | null;
}

export interface PdpDiffDay {
  day: string;
  new: number;
  changed: number;
  unchanged: number;
  existing_not_in_import: number;
}

export interface PdpCommitResult {
  created: number;
  updated: number;
  skipped: number;
  teams_created: number;
  areas_created: number;
}

export interface PdpImportSummary {
  id: string;
  status: PdpImportRecord['status'];
  source_filename: string;
  created_at: string;
}

function _pdpImportBase(festivalId: string): string {
  return `/festivals/${festivalId}/pdp-import`;
}

export async function listPdpImports(
  festivalId: string,
): Promise<PdpImportSummary[]> {
  return request<PdpImportSummary[]>(_pdpImportBase(festivalId));
}

export async function uploadPdpImport(
  festivalId: string,
  file: File,
): Promise<{ id: string; status: string }> {
  const form = new FormData();
  form.append('file', file);
  return request(`${_pdpImportBase(festivalId)}`, { method: 'POST', body: form });
}

export async function getPdpImport(
  festivalId: string,
  importId: string,
): Promise<PdpImportRecord> {
  return request<PdpImportRecord>(`${_pdpImportBase(festivalId)}/${importId}`);
}

export async function patchPdpImportRows(
  festivalId: string,
  importId: string,
  rows: PdpImportPreviewRow[],
): Promise<{ ok: boolean; rows: number }> {
  return request(`${_pdpImportBase(festivalId)}/${importId}/rows`, {
    method: 'PATCH',
    body: JSON.stringify(rows),
  });
}

export async function computePdpImportDiff(
  festivalId: string,
  importId: string,
): Promise<{ days: PdpDiffDay[] }> {
  // Scans the whole existing-entry set like commit does — give it headroom
  // past the 15s default so a big plan's diff never aborts mid-scan.
  return request(`${_pdpImportBase(festivalId)}/${importId}/diff`, {
    method: 'POST',
    timeoutMs: 60_000,
  });
}

export async function commitPdpImport(
  festivalId: string,
  importId: string,
  body: { apply_new: boolean; apply_changed: boolean },
): Promise<PdpCommitResult> {
  return request(`${_pdpImportBase(festivalId)}/${importId}/commit`, {
    method: 'POST',
    body: JSON.stringify(body),
    // The diff-merge writes every new/changed entry in one request — a big
    // plan legitimately took ~15.6s in prod, just over the 15s default, so
    // the client aborted a commit that actually succeeded. Give it room.
    timeoutMs: 180_000,
  });
}

export async function discardPdpImport(
  festivalId: string,
  importId: string,
): Promise<void> {
  await request(`${_pdpImportBase(festivalId)}/${importId}`, { method: 'DELETE' });
}

// --- Support ---

export type SupportCategory = "bug" | "question" | "idea" | "other";

export interface SupportMessagePayload {
  category: SupportCategory;
  /** 10..4000 characters. */
  message: string;
  page_url?: string;
  app_version?: string;
  user_agent?: string;
}

export interface SupportMessageResult {
  id: string;
  status: string;
  created_at: string;
}

/**
 * Send a message to the TRACE team from the in-app Help dialog. The server
 * rate-limits per user and answers 429 with a plain-English `detail`, which
 * `request()` surfaces as the Error message — show it as is.
 */
export async function sendSupportMessage(
  payload: SupportMessagePayload,
): Promise<SupportMessageResult> {
  return request<SupportMessageResult>("/support/messages", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// --- Admin campaigns ---

export type AdminCampaignStatus = "draft" | "sending" | "sent" | "failed";

export interface AdminCampaignAudience {
  /** Subscription tiers to include; `null` = every tier. */
  tiers: string[] | null;
  verified_only: boolean;
  exclude_opt_out: boolean;
}

export interface AdminCampaignListItem {
  id: string;
  subject: string;
  status: AdminCampaignStatus;
  recipients_total: number;
  recipients_sent: number;
  recipients_failed: number;
  created_at: string;
  sent_at: string | null;
  created_by_email: string | null;
}

export interface AdminCampaign extends AdminCampaignListItem {
  preheader: string;
  body_markdown: string;
  cta_label: string;
  cta_url: string;
  audience: AdminCampaignAudience;
  created_by: string | null;
  updated_at: string;
  /** Live count of users the audience resolves to right now. */
  audience_count: number;
}

export interface AdminCampaignPayload {
  subject?: string;
  preheader?: string;
  body_markdown?: string;
  cta_label?: string;
  cta_url?: string;
  audience?: AdminCampaignAudience;
}

export type AdminCampaignPreviewOverrides = Omit<AdminCampaignPayload, "audience">;

export interface AdminCampaignPreview {
  subject: string;
  preheader: string;
  html: string;
}

/** `sending` is the worker's per-row claim; `skipped` = consent re-check at send time. */
export type AdminCampaignRecipientStatus =
  | "pending"
  | "sending"
  | "sent"
  | "failed"
  | "skipped";

export interface AdminCampaignRecipient {
  id: string;
  email: string;
  user_id: string | null;
  status: AdminCampaignRecipientStatus;
  error: string | null;
  sent_at: string | null;
  resend_message_id: string | null;
}

export interface AdminCampaignSendResult {
  campaign_id: string;
  status: AdminCampaignStatus;
  recipients_total: number;
}

export async function getAdminCampaigns(): Promise<AdminCampaignListItem[]> {
  return request<AdminCampaignListItem[]>("/admin/campaigns");
}

export async function createAdminCampaign(
  body: AdminCampaignPayload,
): Promise<AdminCampaign> {
  return request<AdminCampaign>("/admin/campaigns", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function getAdminCampaign(id: string): Promise<AdminCampaign> {
  return request<AdminCampaign>(`/admin/campaigns/${id}`);
}

/** Drafts only — the server answers 409 once a campaign has been sent. */
export async function updateAdminCampaign(
  id: string,
  body: AdminCampaignPayload,
): Promise<AdminCampaign> {
  return request<AdminCampaign>(`/admin/campaigns/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

/** Renders the email for the calling admin; unsaved form values go in as overrides. */
export async function previewAdminCampaign(
  id: string,
  overrides: AdminCampaignPreviewOverrides = {},
): Promise<AdminCampaignPreview> {
  return request<AdminCampaignPreview>(`/admin/campaigns/${id}/preview`, {
    method: "POST",
    body: JSON.stringify(overrides),
  });
}

export async function sendTestAdminCampaign(
  id: string,
): Promise<{ detail: string; to: string }> {
  return request<{ detail: string; to: string }>(
    `/admin/campaigns/${id}/send-test`,
    { method: "POST" },
  );
}

export async function sendAdminCampaign(
  id: string,
  body: { confirm: boolean },
): Promise<AdminCampaignSendResult> {
  return request<AdminCampaignSendResult>(`/admin/campaigns/${id}/send`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function getAdminCampaignRecipients(
  id: string,
  status?: AdminCampaignRecipientStatus,
): Promise<AdminCampaignRecipient[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return request<AdminCampaignRecipient[]>(
    `/admin/campaigns/${id}/recipients${qs}`,
  );
}

export async function retryFailedAdminCampaign(
  id: string,
): Promise<AdminCampaignSendResult> {
  return request<AdminCampaignSendResult>(
    `/admin/campaigns/${id}/retry-failed`,
    { method: "POST" },
  );
}

// --- Marketing emails + admin support inbox ---

export interface MarketingEmailsPreference {
  /** `true` = the user does not want product-update emails. */
  opt_out: boolean;
  /** When the current opt-out was recorded; `null` while opted in. */
  opted_out_at: string | null;
}

export async function getMarketingEmailsPreference(): Promise<MarketingEmailsPreference> {
  return request<MarketingEmailsPreference>("/notifications/marketing-emails");
}

export async function setMarketingEmailsPreference(
  optOut: boolean,
): Promise<MarketingEmailsPreference> {
  return request<MarketingEmailsPreference>("/notifications/marketing-emails", {
    method: "PUT",
    body: JSON.stringify({ opt_out: optOut }),
  });
}

export type AdminSupportMessageStatus = "open" | "closed";
export type AdminSupportStatusFilter = AdminSupportMessageStatus | "all";

export interface AdminSupportMessage {
  id: string;
  /** `null` once the author's account has been deleted (email is a snapshot). */
  user_id: string | null;
  email: string;
  category: SupportCategory;
  message: string;
  page_url: string | null;
  app_version: string | null;
  user_agent: string | null;
  status: AdminSupportMessageStatus;
  created_at: string;
  /** `null` while open, and also when the server does not track it yet. */
  closed_at: string | null;
}

export interface AdminSupportMessagesPage {
  items: AdminSupportMessage[];
  /** Rows matching the status filter, across every page. */
  total: number;
  /** Open messages regardless of the filter — drives the dashboard card. */
  open_count: number;
}

export async function getAdminSupportMessages(
  status: AdminSupportStatusFilter = "open",
  limit = 50,
  offset = 0,
): Promise<AdminSupportMessagesPage> {
  const qs = new URLSearchParams({
    status,
    limit: String(limit),
    offset: String(offset),
  });
  return request<AdminSupportMessagesPage>(`/admin/support-messages?${qs}`);
}

export async function setAdminSupportMessageStatus(
  id: string,
  status: AdminSupportMessageStatus,
): Promise<AdminSupportMessage> {
  return request<AdminSupportMessage>(`/admin/support-messages/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}
